import React, { useEffect, useState, useCallback, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import "./App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// =============== Auth context ===============
const AuthCtx = createContext(null);
const useAuth = () => useContext(AuthCtx);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const setToken = (t) => { if (t) localStorage.setItem("h4u_token", t); else localStorage.removeItem("h4u_token"); };
  const token = () => localStorage.getItem("h4u_token");

  const authHeaders = () => {
    const t = token();
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const refresh = useCallback(async () => {
    const t = localStorage.getItem("h4u_token");
    if (!t) { setUser(null); setLoading(false); return; }
    try {
      const r = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${t}` } });
      setUser(r.data);
    } catch { setUser(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    refresh();
  }, [refresh]);

  const logout = async () => {
    const t = localStorage.getItem("h4u_token");
    try { await axios.post(`${API}/auth/logout`, {}, { headers: t ? { Authorization: `Bearer ${t}` } : {} }); } catch (e) { console.debug("[hope4us:swallowed]", e); }
    setToken(null); setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, refresh, setToken, authHeaders, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

const apiGet  = (path, headers) => axios.get(`${API}${path}`, { headers });
const apiPost = (path, body, headers) => axios.post(`${API}${path}`, body, { headers });

// =============== Dismissable soft safety banner (short, calm-colored) ===============
function SafetyBanner({ text, testId }) {
  const [open, setOpen] = useState(true);
  if (!text || !open) return null;
  const short = text.length > 180 ? text.slice(0, 176).trimEnd() + "…" : text;
  return (
    <div className="h4u-banner soft mt-3" data-testid={testId || "safety-banner"} style={{paddingRight:36}}>
      <div className="h-sans" style={{lineHeight:1.55}}>{short}</div>
      <button className="h4u-banner-close" onClick={()=>setOpen(false)} data-testid={`${testId || "safety-banner"}-close`} aria-label="Dismiss">✕</button>
    </div>
  );
}

// =============== Agreement gate (popup shown on every app open until accepted) ===============
function AgreementGate() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const accepted = localStorage.getItem("h4u_terms_v2_accepted") === "1";
      setOpen(!accepted);
    } catch { setOpen(true); }
  }, []);

  // Never block the legal/contact pages themselves so users can review terms if they navigated directly.
  const onLegalRoute = location.pathname === "/legal" || location.pathname === "/contact";

  const accept = () => {
    try { localStorage.setItem("h4u_terms_v2_accepted", "1"); } catch (e) { console.debug("[hope4us:swallowed]", e); }
    setOpen(false);
  };
  const viewTerms = () => {
    // Keep the popup open behind — user must still click I AGREE after reviewing.
    navigate("/legal");
    setOpen(false);
  };

  if (!open || onLegalRoute) return null;

  return (
    <div
      style={{position:"fixed", inset:0, zIndex:9999, background:"rgba(30,25,20,0.55)", backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center", padding:"24px"}}
      data-testid="agreement-gate"
    >
      <div className="h4u-card p-8" style={{maxWidth:"480px", background:"#fffdf7", boxShadow:"0 24px 60px rgba(0,0,0,0.25)"}}>
        <div className="flex items-center gap-3 mb-4">
          <img src="/logo/hope4us-logo-mark.png" alt="Hope4US" style={{width:44, height:44, objectFit:"contain"}} />
          <div>
            <div className="h-sans" style={{color:"var(--muted)", fontSize:11, letterSpacing:"0.1em", textTransform:"uppercase"}}>Before you continue</div>
            <div className="text-xl" style={{fontWeight:600}}>Welcome to Hope4US</div>
          </div>
        </div>
        <p className="h-sans" style={{color:"var(--ink-soft)", lineHeight:1.65, fontSize:15}}>
          By continuing, you agree to our <button onClick={viewTerms} className="h4u-link" style={{background:"none", border:"none", padding:0, cursor:"pointer", font:"inherit"}}>Release of Liability</button> and <button onClick={viewTerms} className="h4u-link" style={{background:"none", border:"none", padding:0, cursor:"pointer", font:"inherit"}}>Privacy Policy</button>.
        </p>
        <div className="flex gap-3 mt-6">
          <button onClick={accept} className="h4u-btn h4u-btn-primary flex-1" data-testid="agreement-accept">I AGREE</button>
          <button onClick={viewTerms} className="h4u-btn h4u-btn-ghost flex-1" data-testid="agreement-view-terms">View Terms</button>
        </div>
      </div>
    </div>
  );
}

// =============== Layout ===============
function Shell({ children }) {
  const { user, logout } = useAuth();
  return (
    <div className="h4u-grain min-h-screen">
      <header className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
          <img src="/logo/hope4us-logo-mark.png" alt="Hope4US" style={{height:44, width:"auto", objectFit:"contain"}} data-testid="brand-logo" />
          <div className="leading-tight" style={{display:"none"}}>
            <div className="h-sans" style={{fontWeight:600, fontSize:18}}>Hope4US</div>
            <div style={{fontSize:11, color:"var(--muted)", letterSpacing:"0.08em", textTransform:"uppercase"}} className="h-sans">Giving Love a Foundation</div>
          </div>
        </Link>
        <nav className="flex items-center gap-3 h-sans" style={{fontSize:14}}>
          {user ? (
            <>
              <Link to="/dashboard" className="h4u-btn h4u-btn-ghost" data-testid="nav-dashboard">Dashboard</Link>
              <Link to="/practices" className="h4u-btn h4u-btn-ghost" data-testid="nav-practices">Practice</Link>
              <Link to="/progress" className="h4u-btn h4u-btn-ghost" data-testid="nav-progress">Progress</Link>
              <Link to="/settings" className="h4u-btn h4u-btn-ghost" data-testid="nav-settings">Settings</Link>
              <Link to="/referrals" className="h4u-btn h4u-btn-ghost" data-testid="nav-referrals">Refer</Link>
              <Link to="/resources" className="h4u-btn h4u-btn-ghost" data-testid="nav-resources">Resources</Link>
              <button onClick={logout} className="h4u-btn h4u-btn-ghost" data-testid="nav-logout">Sign out</button>
            </>
          ) : (
            <>
              <Link to="/resources" className="h4u-link" data-testid="nav-resources">Resources</Link>
              <Link to="/auth" className="h4u-btn h4u-btn-primary" data-testid="nav-auth">Begin / Log in</Link>
            </>
          )}
        </nav>
      </header>
      <main className="max-w-5xl mx-auto px-6 pb-24">{children}</main>
      <footer className="max-w-5xl mx-auto px-6 py-10 text-sm" style={{color:"var(--muted)"}}>
        Hope4US is an educational and relationship-insight tool. It is not therapy, medical or legal advice, diagnosis, or a crisis service. <Link to="/resources" className="h4u-link">If you would like to find out more about professional guidance, or if you ever feel like you're in danger, see our Resources page →</Link>
      </footer>
      <div style={{position:"fixed", bottom:14, left:16, fontFamily:"'Outfit', sans-serif", fontSize:11, color:"var(--muted)", letterSpacing:"0.04em", zIndex:10, pointerEvents:"none"}} data-testid="og-copyright">© OG · Hope4US</div>
    </div>
  );
}

// =============== Landing ===============
function Landing() {
  return (
    <Shell>
      {/* Hero — big centered logo + punchy sales copy */}
      <section className="pt-6 pb-4 text-center fade-in">
        <img
          src="/logo/hope4us-logo-header.png"
          alt="Hope4US · Giving Love a Foundation"
          data-testid="landing-hero-logo"
          style={{width:"min(360px, 62vw)", height:"auto", margin:"0 auto", display:"block"}}
        />
      </section>

      <section className="pt-4 fade-in">
        <div className="h4u-chip mb-6" data-testid="landing-eyebrow">The newest, greatest thing any relationship has ever seen.</div>

        <h1 className="text-5xl md:text-6xl" style={{lineHeight:1.05, fontWeight:500, letterSpacing:"-0.02em"}}>
          What is <em style={{fontStyle:"italic", color:"var(--terracotta)"}}>Hope4US</em>?
        </h1>

        <p className="mt-6 max-w-3xl h-sans" style={{fontSize:20, color:"var(--ink)", lineHeight:1.5, fontWeight:500}}>
          Hope4US is your relationship's new best friend.
        </p>

        <p className="mt-5 max-w-3xl h-sans" style={{fontSize:18, color:"var(--ink-soft)", lineHeight:1.6}}>
          Are you struggling? Thinking about getting married? Just friends wondering if you're ready to take the next step? Together fifteen years and quietly drifting? Freshly heartbroken and rebuilding? <strong>Hope4US is for every one of you.</strong>
        </p>

        <p className="mt-5 max-w-3xl h-sans" style={{fontSize:18, color:"var(--ink-soft)", lineHeight:1.6}}>
          Hope4US <strong>isn't counseling</strong>. It isn't a workbook. It isn't a personality quiz, a couples retreat, or a stack of homework. And it never — <em>ever</em> — takes sides. Hope4US is your <strong style={{color:"var(--terracotta)"}}>guide</strong> to actually understanding each other. Because communication is the number-one thing every successful couple has in common — and how are the two of you supposed to build anything together if you're speaking different languages?
        </p>

        <p className="mt-5 max-w-3xl h-sans" style={{fontSize:20, color:"var(--ink)", lineHeight:1.5, fontStyle:"italic", fontWeight:500}} data-testid="translator-line">
          Hope4US is your translator — guiding you through learning each other's language.
        </p>

        <p className="mt-5 max-w-3xl h-sans" style={{fontSize:18, color:"var(--ink-soft)", lineHeight:1.6}}>
          Here's how it works. We start with the analysis — two private intakes, one honest look at what's actually going on between you. Then, from there, <strong>every couple gets a program tailored to them</strong>. Not a template. Not a script. A custom, guided experience that walks the two of you through it, step by step, in real time, on your own devices — and evolves with you as you grow.
        </p>

        <div className="mt-8 flex flex-wrap gap-3 items-center">
          <Link to="/auth" className="h4u-btn h4u-btn-primary" data-testid="cta-begin" style={{fontSize:16, padding:"14px 28px"}}>Start free · takes 10 minutes</Link>
        </div>

        <div className="mt-4 h-sans" style={{color:"var(--muted)", fontSize:14}}>
          Only one of you feeling ready right now? Start alone. You'll get real value on day one — and there's a natural, no-pressure moment to invite your partner when you're ready.
        </div>
      </section>

      {/* Differentiators */}
      <section className="mt-20">
        <div className="h-sans" style={{color:"var(--muted)", letterSpacing:"0.08em", textTransform:"uppercase", fontSize:12}}>Why Hope4US is different</div>
        <h2 className="text-3xl mt-2" style={{fontWeight:500}}>There has literally never been anything like this.</h2>
        <div className="grid md:grid-cols-3 gap-5 mt-6">
          {[
            {t:"Built only for you two", d:"No two couples get the same program. Every question, every practice, every reflection is generated for the couple in front of us — young or seasoned, thriving or hurting, quiet or fiery. The program bends to fit you, not the other way around."},
            {t:"Two devices. One private space.", d:"Each of you has your own private intake. Neither sees the other's raw answers. What comes back is a shared, honest picture of how the two of you actually work together — nothing accusatory, nothing exposed, nobody's the bad guy."},
            {t:"Live, guided practices — not homework", d:"You don't get a PDF and a good-luck. You get walked through every moment together, on your own devices, in real time. Feeling check-ins in every step tell the program how you're really doing so it knows where to guide you next."},
          ].map((c,i)=>(
            <div key={i} className="h4u-card p-7 fade-in" data-testid={`differentiator-${i}`}>
              <div className="h-sans" style={{fontSize:13, color:"var(--terracotta)", letterSpacing:"0.08em", textTransform:"uppercase"}}>0{i+1}</div>
              <div className="mt-2 text-2xl" style={{fontWeight:500}}>{c.t}</div>
              <p className="mt-3 h-sans" style={{color:"var(--ink-soft)", lineHeight:1.6}}>{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* What we draw from */}
      <section className="mt-20">
        <div className="h-sans" style={{color:"var(--muted)", letterSpacing:"0.08em", textTransform:"uppercase", fontSize:12}}>Under the hood</div>
        <h2 className="text-3xl mt-2" style={{fontWeight:500}}>Decades of research. One quiet, personal program.</h2>
        <p className="mt-3 max-w-3xl h-sans" style={{fontSize:16, color:"var(--ink-soft)", lineHeight:1.6}}>
          Hope4US quietly weaves together the best of modern relationship science — the Gottman body of work on what actually predicts lasting bonds, DBT-informed emotional regulation skills, attachment-style research, love-language frameworks, and clinical patterns drawn from decades of couples work — and translates it into something you can actually <em>do</em> together, in your living room, at your own pace.
        </p>
        <div className="grid md:grid-cols-4 gap-4 mt-6">
          {[
            {t:"Gottman-informed patterns", d:"What predicts lasting bonds — and what quietly erodes them."},
            {t:"DBT-informed skills", d:"Practical, in-the-moment tools for staying steady when things get hot."},
            {t:"Attachment science", d:"Why you two react the way you do when closeness or distance shows up."},
            {t:"Love-language frameworks", d:"Learning to speak the way the other person actually hears."},
          ].map((c,i)=>(
            <div key={i} className="p-5 rounded-xl" style={{background:"#f3efe3", border:"1px solid var(--line)"}} data-testid={`method-${i}`}>
              <div className="h-sans" style={{fontWeight:600}}>{c.t}</div>
              <p className="mt-2 h-sans" style={{color:"var(--ink-soft)", lineHeight:1.55, fontSize:14}}>{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Audience */}
      <section className="mt-20">
        <div className="h-sans" style={{color:"var(--muted)", letterSpacing:"0.08em", textTransform:"uppercase", fontSize:12}}>Who this is for</div>
        <h2 className="text-3xl mt-2" style={{fontWeight:500}}>If you love someone, this is for you.</h2>
        <div className="grid md:grid-cols-2 gap-5 mt-5">
          {[
            {t:"Just starting out",   d:"Find out what the two of you are already great at — and where you'll want to be gentle with each other — long before anything gets stuck."},
            {t:"Thinking about forever", d:"Money, family, kids, intimacy, quiet dealbreakers — surface it privately, together, before the vows."},
            {t:"Steady, but curious",  d:"Healthy couples don't stay healthy by accident. Keep the good habits sharp and spot small shifts before they grow."},
            {t:"Going through something hard", d:"A private way to actually be heard, on both sides, without one person having to be right and the other wrong."},
            {t:"After a rough patch",  d:"When things have been shaky, Hope4US gives you a slower, safer path back to seeing each other honestly."},
            {t:"Considering going separate ways", d:"Get clarity without pressure. Sometimes the honest answer is 'stay and grow,' sometimes it's 'part with grace.' The program supports either."},
          ].map((c,i)=>(
            <div key={i} className="h4u-card p-6" data-testid={`audience-${i}`}>
              <div className="h-sans" style={{fontWeight:600, fontSize:16}}>{c.t}</div>
              <p className="mt-2 h-sans" style={{color:"var(--ink-soft)", lineHeight:1.6}}>{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="mt-20">
        <div className="h-sans" style={{color:"var(--muted)", letterSpacing:"0.08em", textTransform:"uppercase", fontSize:12}}>How it works</div>
        <h2 className="text-3xl mt-2" style={{fontWeight:500}}>Ten minutes. Two devices. A program that's yours forever.</h2>
        <div className="grid md:grid-cols-4 gap-5 mt-6">
          {[
            "You answer privately",
            "Your partner answers privately",
            "Hope4US listens to both sides — neutrally",
            "You each receive a program built only for the two of you",
          ].map((s,i)=>(
            <div key={i} className="h4u-card p-6">
              <div className="text-3xl" style={{color:"var(--sage-2)"}}>0{i+1}</div>
              <div className="mt-2 h-sans" style={{fontWeight:500}}>{s}</div>
            </div>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/auth" className="h4u-btn h4u-btn-primary" data-testid="cta-begin-2" style={{fontSize:16, padding:"14px 28px"}}>Start free</Link>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="mt-20 h4u-card p-7" style={{background:"#f3efe3"}}>
        <div className="h-sans" style={{color:"var(--muted)", letterSpacing:"0.08em", textTransform:"uppercase", fontSize:12}}>A gentle disclaimer</div>
        <p className="mt-3 h-sans" style={{color:"var(--ink-soft)", lineHeight:1.7, fontSize:15}}>
          Hope4US is an educational, self-reflective relationship-insight platform. It is <strong>not</strong> therapy, counseling, medical or legal advice, diagnosis, lie detection, an abuse-screening service, or a crisis service. Everything you see here is offered to help the two of you understand each other better and act with more care — not to render professional opinions or replace qualified help when qualified help is what's needed. By using Hope4US you and your partner agree that participation is voluntary, that insights are educated hypotheses based on what you tell us, and that final decisions about your relationship, safety, health, family, or finances remain entirely your own. If you would like to find out more about professional guidance, or if you ever feel like you're in danger, check out our <Link to="/resources" className="h4u-link">Resources page</Link> for more help. Full <Link to="/legal" className="h4u-link">Terms of Use, Release of Liability, and Privacy Policy</Link>.
        </p>
      </section>

      {/* Contact — solo at the bottom */}
      <section className="mt-16 mb-4 text-center" data-testid="landing-contact-section">
        <Link to="/contact" className="h4u-btn h4u-btn-ghost" data-testid="cta-contact" style={{fontSize:16, padding:"14px 28px"}}>Contact us</Link>
      </section>
    </Shell>
  );
}

function About() {
  // Route removed per product decision — redirect any lingering links to the landing page.
  return <Navigate to="/" replace />;
}

// =============== Auth ===============
function AuthScreen() {
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState(""); const [password, setPassword] = useState(""); const [name, setName] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { setToken, refresh } = useAuth();
  const navigate = useNavigate();
  const referralCode = (new URLSearchParams(window.location.search)).get("ref");

  useEffect(() => { if (referralCode) setMode("signup"); }, [referralCode]);

  // Prefill accepted-state from prior gate acknowledgement so returning users don't have to re-check.
  useEffect(() => {
    try { if (localStorage.getItem("h4u_terms_v2_accepted") === "1") setAccepted(true); } catch (e) { console.debug("[hope4us:swallowed]", e); }
  }, []);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const path = mode === "signup" ? "/auth/signup" : "/auth/login";
      const body = mode === "signup"
        ? { email, password, name, accepted_terms: accepted, referral_code: referralCode || undefined }
        : { email, password };
      const r = await apiPost(path, body);
      setToken(r.data.token);
      await refresh();
      navigate("/dashboard");
    } catch (e) { setErr(e?.response?.data?.detail || "Something went wrong."); }
    finally { setBusy(false); }
  };

  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submitDisabled = busy || !accepted;
  const googleDisabled = !accepted;

  return (
    <Shell>
      <div className="max-w-md mx-auto h4u-card p-8 mt-8 fade-in">
        <div className="flex gap-2 mb-6">
          <button onClick={()=>setMode("signin")} className={`h4u-btn ${mode==="signin"?"h4u-btn-primary":"h4u-btn-ghost"}`} data-testid="auth-tab-signin">Sign in</button>
          <button onClick={()=>setMode("signup")} className={`h4u-btn ${mode==="signup"?"h4u-btn-primary":"h4u-btn-ghost"}`} data-testid="auth-tab-signup">Create account</button>
        </div>
        {referralCode && mode === "signup" && <div className="h4u-banner mb-4" data-testid="referral-applied">A friend referred you with code <strong>{referralCode}</strong>. When you subscribe to Premium, you and your friend each get a free month.</div>}
        <form onSubmit={submit} className="space-y-3">
          {mode === "signup" && <input className="h4u-input" placeholder="Your name (optional)" value={name} onChange={e=>setName(e.target.value)} data-testid="auth-name" />}
          <input className="h4u-input" type="email" placeholder="Email" value={email} onChange={e=>setEmail(e.target.value)} required data-testid="auth-email" />
          <input className="h4u-input" type="password" placeholder="Password" value={password} onChange={e=>setPassword(e.target.value)} required data-testid="auth-password" />
          <label className="flex gap-3 items-start text-sm h-sans" style={{color:"var(--ink-soft)"}}>
            <input type="checkbox" checked={accepted} onChange={e=>setAccepted(e.target.checked)} className="mt-1" data-testid="auth-accept" />
            <span>
              I have read and accept the <Link to="/legal" className="h4u-link" target="_blank">Terms of Use, Release of Liability & Privacy Policy</Link> in full. I understand Hope4US is not therapy, legal or medical advice, diagnosis, lie detection, or a crisis service.
            </span>
          </label>
          {err && <div className="h4u-banner danger" data-testid="auth-error">{err}</div>}
          <button disabled={submitDisabled} className="h4u-btn h4u-btn-primary w-full" data-testid="auth-submit">
            {busy ? "Working…" : (mode === "signup" ? "Create account" : "Sign in")}
          </button>
        </form>
        <div className="my-4 text-center h-sans text-sm" style={{color:"var(--muted)"}}>or</div>
        <button onClick={googleLogin} disabled={googleDisabled} className="h4u-btn h4u-btn-ghost w-full" data-testid="auth-google">Continue with Google</button>
        {!accepted && <div className="h-sans mt-3 text-sm" style={{color:"var(--muted)", textAlign:"center"}} data-testid="terms-gate-hint">Please accept the Terms above to enable sign-in.</div>}
      </div>
    </Shell>
  );
}

function AuthCallback() {
  // Synchronously processes the session_id fragment from Emergent OAuth.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const navigate = useNavigate();
  const { setToken, refresh } = useAuth();
  const [error, setError] = useState("");
  useEffect(() => {
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) { navigate("/auth"); return; }
    const session_id = decodeURIComponent(m[1]);
    (async () => {
      try {
        const r = await apiPost("/auth/google/session", { session_id });
        if (r.data?.session_token) {
          setToken(r.data.session_token);
          await refresh();
        }
        window.location.replace("/dashboard");
      } catch (e) { setError("Could not complete Google sign-in."); }
    })();
  }, [navigate, setToken, refresh]);
  return (<Shell><div className="h4u-card p-8 mt-10 max-w-md mx-auto">{error || "Finishing sign-in…"}</div></Shell>);
}

function Legal({ scrollGate = false, onAccepted, showAcceptControl = false }) {
  const scrollRef = React.useRef(null);
  const [reachedBottom, setReachedBottom] = useState(!scrollGate);
  const [accepted, setAccepted] = useState(false);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 32) setReachedBottom(true);
  };

  const doAccept = () => {
    if (!reachedBottom || !accepted) return;
    try { localStorage.setItem("h4u_terms_v2_accepted", "1"); } catch (e) { console.debug("[hope4us:swallowed]", e); }
    if (onAccepted) onAccepted();
  };

  const body = (
    <>
      <h2 className="text-3xl mt-6" style={{fontWeight:500}} data-testid="legal-heading">Terms of Use, Release of Liability & Privacy Policy</h2>
      <p className="h-sans mt-2" style={{color:"var(--muted)", fontSize:13}}>Effective date: {new Date().toLocaleDateString(undefined, {year:'numeric', month:'long', day:'numeric'})}</p>

      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="h4u-card p-7 mt-6 h-sans"
        style={{lineHeight:1.7, color:"var(--ink-soft)", maxHeight: scrollGate ? "60vh" : "none", overflowY: scrollGate ? "auto" : "visible"}}
        data-testid="legal-body"
      >
        <h3 className="h-sans text-xl mt-0" style={{color:"var(--ink)"}}>1. Nature of the Service</h3>
        <p>Hope4US is an educational and self-reflective relationship-insight platform. Hope4US is not therapy, counseling, medical care, legal advice, financial advice, diagnosis, lie detection, abuse-screening, or a crisis-intervention service. Any insights, patterns, or suggestions produced by Hope4US are educated hypotheses derived from the information users voluntarily provide, and must not be relied upon as clinical, medical, legal, or safety determinations.</p>

        <h3 className="h-sans text-xl" style={{color:"var(--ink)"}}>2. Release of Liability</h3>
        <p>By accessing or using Hope4US, each user acknowledges and voluntarily assumes all risks associated with such use. Hope4US does not verify the accuracy, completeness, or truthfulness of information submitted by any party, and no output produced by the service should be interpreted as a professional opinion. Users agree that decisions regarding their relationships, health, safety, family, finances, or legal circumstances remain solely their own responsibility.</p>
        <p>Hope4US, together with its operators, affiliates, employees, contractors, service providers, and contributors, shall not be liable for any direct, indirect, incidental, consequential, special, or exemplary damages, including but not limited to emotional, relational, reputational, financial, or physical harm arising out of, or in connection with, the use or inability to use the service, whether based in contract, tort, statute, or otherwise, and whether or not Hope4US was advised of the possibility of such damages, to the maximum extent permitted by applicable law.</p>
        <p><strong>Hope4US disclaims liability from anything claimed as a result of our service.</strong></p>

        <h3 className="h-sans text-xl" style={{color:"var(--ink)"}}>3. Privacy Policy</h3>
        <p><strong>3.1 Information We Collect.</strong> Hope4US collects (a) account information provided at registration (including electronic mail address and any voluntarily submitted display name), (b) intake responses and practice-session responses submitted by the user, (c) authentication metadata generated by our identity providers, and (d) minimal technical telemetry required to operate the service (session identifiers, request timestamps, and error diagnostics).</p>
        <p><strong>3.2 Use of Information.</strong> Personal information is processed solely for the purposes of (i) delivering the requested service, (ii) generating personalized insights and practice sessions, (iii) maintaining security, integrity, and continuity of the platform, (iv) satisfying legal, regulatory, or judicial obligations, and (v) enforcing our Terms of Use. Hope4US does not sell personal information and does not disclose personal information to third parties for marketing purposes.</p>
        <p><strong>3.3 Confidentiality Between Partners.</strong> Each partner's raw responses are visible solely to that partner. Shared reports and shared session outputs are algorithmically generated at a system-level and do not attribute statements or verbatim quotations to either partner. Private reports and private session takeaways are visible only to the participant to whom they pertain.</p>
        <p><strong>3.4 Data Retention.</strong> Personal data is retained for the duration of the account relationship and thereafter for such period as is necessary to comply with applicable legal, regulatory, and record-keeping obligations. Users may request access to, correction of, or deletion of their personal data by contacting Hope4US through the channels provided in the application.</p>
        <p><strong>3.5 Security.</strong> Hope4US employs industry-standard administrative, technical, and physical safeguards designed to protect personal information against unauthorized access, alteration, disclosure, or destruction. No electronic transmission or storage system, however, can be guaranteed to be completely secure, and users acknowledge this inherent risk.</p>
        <p><strong>3.6 Reporting Obligations.</strong> Hope4US does not actively monitor user submissions for law-enforcement purposes. Hope4US reserves the right to disclose information (i) as required by valid legal process, (ii) where necessary to protect the rights, property, or safety of Hope4US, its users, or the public, and (iii) in circumstances involving credible threats of imminent harm, including but not limited to threats involving minors.</p>
        <p><strong>3.7 Modifications.</strong> Hope4US may modify these Terms and this Privacy Policy from time to time. Continued use of the service following notice of such modifications constitutes acceptance of the revised terms.</p>

        <h3 className="h-sans text-xl" style={{color:"var(--ink)"}}>4. Guidelines for Use</h3>
        <p>Users agree to answer intake and session prompts honestly to the best of their ability, to refrain from submitting content pertaining to third parties without their knowledge, and to use Hope4US in a manner consistent with lawful and non-abusive conduct. Hope4US may suspend or terminate accounts that violate these Guidelines.</p>

        <h3 className="h-sans text-xl" style={{color:"var(--ink)"}}>5. Copyright and Intellectual Property</h3>
        <p>© {new Date().getFullYear()} OG · Hope4US. All rights reserved. The Hope4US name, logo, marks, content, prompts, question framework, and algorithmic outputs are protected by applicable intellectual-property laws. No portion of the service may be reproduced, distributed, or used to train derivative models without prior written authorization.</p>

        <h3 className="h-sans text-xl" style={{color:"var(--ink)"}}>6. Contact</h3>
        <p>Questions, requests, or notices under this policy may be directed to Hope4US via the in-app <Link to="/contact" className="h4u-link">Contact page</Link>.</p>
      </div>

      {showAcceptControl && (
        <div className="h4u-card p-6 mt-4" data-testid="legal-accept-panel">
          {!reachedBottom && <div className="h-sans" style={{color:"var(--muted)", fontSize:13}}>Please scroll to the end of the document to enable the acceptance checkbox.</div>}
          <label className="flex gap-3 items-start text-sm h-sans mt-2" style={{color: reachedBottom ? "var(--ink-soft)" : "var(--muted)", opacity: reachedBottom ? 1 : 0.55}}>
            <input type="checkbox" disabled={!reachedBottom} checked={accepted} onChange={e=>setAccepted(e.target.checked)} className="mt-1" data-testid="legal-accept-checkbox" />
            <span>I have read, understood, and accept the Terms of Use, Release of Liability, and Privacy Policy in full.</span>
          </label>
          <button
            className="h4u-btn h4u-btn-primary mt-4"
            disabled={!reachedBottom || !accepted}
            onClick={doAccept}
            data-testid="legal-accept-btn"
          >
            Accept & continue
          </button>
        </div>
      )}
    </>
  );

  if (showAcceptControl && !onAccepted) {
    // Full-page standalone view: wrap in Shell.
    return <Shell>{body}</Shell>;
  }
  return showAcceptControl ? body : <Shell>{body}</Shell>;
}

// =============== Contact ===============
function Contact() {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      await apiPost("/contact/message", { email, message });
      setSent(true); setEmail(""); setMessage("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not send. Please try again.");
    } finally { setBusy(false); }
  };

  return (<Shell>
    <div className="mt-6 max-w-3xl">
      <div className="h-sans" style={{color:"var(--muted)", letterSpacing:"0.08em", textTransform:"uppercase", fontSize:12}}>Contact</div>
      <h2 className="text-3xl mt-1" style={{fontWeight:500}}>Get in touch with Hope4US</h2>
      <p className="mt-3 h-sans" style={{color:"var(--ink-soft)", lineHeight:1.6}}>
        Questions, feedback, partnership inquiries, or requests under our privacy policy — send us a note. Someone reads every message.
      </p>

      <div className="h4u-card p-7 mt-6" data-testid="contact-form-card">
        {sent ? (
          <div data-testid="contact-sent">
            <div className="h4u-chip" style={{background:"var(--sage-2)", color:"#fffdf7", border:"none"}}>Message sent</div>
            <p className="mt-3 h-sans" style={{color:"var(--ink-soft)", lineHeight:1.6}}>Thank you — we've received your note and will be in touch at the email you provided.</p>
            <button className="h4u-btn h4u-btn-ghost mt-4" onClick={()=>setSent(false)} data-testid="contact-send-another">Send another</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <div className="h-sans" style={{color:"var(--muted)", fontSize:12, textTransform:"uppercase", letterSpacing:"0.08em"}}>Your email</div>
              <input required type="email" className="h4u-input mt-1" value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@example.com" data-testid="contact-email" />
            </div>
            <div>
              <div className="h-sans" style={{color:"var(--muted)", fontSize:12, textTransform:"uppercase", letterSpacing:"0.08em"}}>Your message</div>
              <textarea required className="h4u-textarea mt-1" value={message} onChange={e=>setMessage(e.target.value)} placeholder="How can we help?" style={{minHeight:160}} data-testid="contact-message" />
            </div>
            {err && <div className="h4u-banner danger" data-testid="contact-error">{err}</div>}
            <button disabled={busy} className="h4u-btn h4u-btn-primary" data-testid="contact-submit">{busy ? "Sending…" : "Send message"}</button>
          </form>
        )}
      </div>

      <div className="h4u-card p-7 mt-8">
        <h3 className="text-xl" style={{fontWeight:500}}>Privacy Policy · Release of Liability · Guidelines · Copyright</h3>
        <p className="mt-3 h-sans" style={{color:"var(--ink-soft)", lineHeight:1.65}}>
          By using Hope4US, you have agreed to our full <Link to="/legal" className="h4u-link">Terms of Use, Release of Liability, and Privacy Policy</Link>. In summary: Hope4US is an educational relationship-insight platform — not therapy, medical or legal advice, diagnosis, or a crisis service. <strong>Hope4US disclaims liability from anything claimed as a result of our service.</strong> Each partner's private answers stay private to that partner. We do not sell your data and we do not report user submissions to authorities except where required by law or where credible threats of harm exist (especially involving minors). Guidelines: use Hope4US honestly and in good faith; do not misuse the platform to harm, deceive, or coerce another person.
        </p>
        <p className="mt-4 h-sans" style={{color:"var(--muted)", fontSize:13}}>© {new Date().getFullYear()} OG · Hope4US. All rights reserved.</p>
      </div>
    </div>
  </Shell>);
}



// =============== Dashboard ===============
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Shell><div className="h4u-card p-8 mt-10">Loading…</div></Shell>;
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

function Dashboard() {
  const { user, authHeaders } = useAuth();
  const [couple, setCouple] = useState(null);
  const [members, setMembers] = useState([]);
  const [modes, setModes] = useState([]);
  const [pickedMode, setPickedMode] = useState("");
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const c = await apiGet("/couple/me", authHeaders());
    setCouple(c.data.couple); setMembers(c.data.members || []);
    const s = await apiGet("/intake/structure");
    setModes(s.data.modes || []);
  }, [authHeaders]);
  useEffect(() => { load(); }, [load]);

  const createCouple = async () => {
    setErr(""); if (!pickedMode) return setErr("Pick a mode first.");
    try { const r = await apiPost("/couple/create", { mode: pickedMode }, authHeaders()); setCouple(r.data.couple); load(); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not create."); }
  };
  const joinCouple = async () => {
    setErr("");
    try { const r = await apiPost("/couple/join", { invite_code: code }, authHeaders()); setCouple(r.data.couple); load(); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not join."); }
  };

  return (
    <Shell>
      <div className="mt-6">
        <div className="h-sans" style={{color:"var(--muted)", letterSpacing:"0.08em", textTransform:"uppercase", fontSize:12}}>Welcome</div>
        <h2 className="text-3xl" style={{fontWeight:500}}>Hi {user?.name || user?.email}</h2>
      </div>

      {!couple ? (
        <div className="grid md:grid-cols-2 gap-5 mt-8">
          <div className="h4u-card p-7" data-testid="create-card">
            <div className="text-2xl" style={{fontWeight:500}}>Start a new intake</div>
            <p className="h-sans mt-2" style={{color:"var(--ink-soft)"}}>Pick the mode that fits you most. You can change emphasis later.</p>
            <div className="mt-4 grid grid-cols-1 gap-2">
              {modes.map(m => (
                <label key={m.id} className={`choice-row ${pickedMode===m.id?"selected":""}`} data-testid={`mode-${m.id}`}>
                  <input type="radio" name="mode" className="mr-2" checked={pickedMode===m.id} onChange={()=>setPickedMode(m.id)} />
                  <strong>{m.name}</strong> — <span style={{color:"var(--ink-soft)"}}>{m.desc}</span>
                </label>
              ))}
            </div>
            <button onClick={createCouple} className="h4u-btn h4u-btn-primary mt-5" data-testid="create-couple">Create my intake</button>
          </div>
          <div className="h4u-card p-7" data-testid="join-card">
            <div className="text-2xl" style={{fontWeight:500}}>Join your partner</div>
            <p className="h-sans mt-2" style={{color:"var(--ink-soft)"}}>Enter the 6-character invite code your partner shared with you.</p>
            <input className="h4u-input mt-4 uppercase" placeholder="ABC123" maxLength={6} value={code} onChange={e=>setCode(e.target.value.toUpperCase())} data-testid="invite-code" />
            <button onClick={joinCouple} className="h4u-btn h4u-btn-primary mt-4" data-testid="join-couple">Join</button>
          </div>
          {err && <div className="h4u-banner danger md:col-span-2">{err}</div>}
        </div>
      ) : (
        <CoupleHome couple={couple} members={members} onChange={load} />
      )}
    </Shell>
  );
}

function CoupleHome({ couple, members, onChange }) {
  const { authHeaders, user } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nudgeStatus, setNudgeStatus] = useState(null);
  const [nudgeBusy, setNudgeBusy] = useState(false);
  const [nudgeMsg, setNudgeMsg] = useState("");
  const navigate = useNavigate();
  useEffect(() => { (async()=>{
    try{
      const r = await apiGet("/intake/state", authHeaders()); setState(r.data);
      const ns = await apiGet("/couple/nudge/status", authHeaders()); setNudgeStatus(ns.data);
    } finally { setLoading(false); }
  })(); }, [authHeaders]);

  const partner = members.find(m => m.user_id !== user.user_id);
  const both = members.length === 2;
  const youDone = state?.complete;

  const sendNudge = async () => {
    setNudgeBusy(true); setNudgeMsg("");
    try {
      await apiPost("/couple/nudge", { kind: "intake" }, authHeaders());
      setNudgeMsg("Nudge sent — your partner will see it next time they open Hope4US.");
      const ns = await apiGet("/couple/nudge/status", authHeaders()); setNudgeStatus(ns.data);
    } catch (e) { setNudgeMsg(e?.response?.data?.detail || "Could not send nudge."); }
    finally { setNudgeBusy(false); }
  };

  // Email-based partner invite state
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteNote, setInviteNote] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteMsg, setInviteMsg] = useState("");
  const [inviteOk, setInviteOk] = useState(false);
  const sendInviteEmail = async () => {
    setInviteBusy(true); setInviteMsg(""); setInviteOk(false);
    try {
      const r = await apiPost("/couple/invite-email",
        { partner_email: inviteEmail, personal_note: inviteNote || null },
        authHeaders());
      if (r.data.email_delivered) {
        setInviteOk(true);
        setInviteMsg(`Invite sent to ${inviteEmail}. They'll get a link + your code (${couple.invite_code}).`);
      } else if (!r.data.email_configured) {
        setInviteMsg("Email not configured yet — share the code above with your partner directly.");
      } else {
        setInviteMsg(`We couldn't deliver the email${r.data.error ? ` (${r.data.error})` : ""}. Share the code above instead.`);
      }
    } catch (e) {
      setInviteMsg(e?.response?.data?.detail || "Could not send invite.");
    } finally { setInviteBusy(false); }
  };

  return (
    <div className="mt-8">
      <div className="h4u-card p-7">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="h-sans" style={{color:"var(--muted)", textTransform:"uppercase", letterSpacing:"0.08em", fontSize:12}}>Your couple · {couple.tier || "free"} tier</div>
            <div className="text-2xl" style={{fontWeight:500}}>{members.map(m=>m.name||m.email).join(" & ")}</div>
          </div>
          <div className="text-right">
            <div className="h-sans" style={{color:"var(--muted)", fontSize:12, textTransform:"uppercase", letterSpacing:"0.08em"}}>Invite code</div>
            <div className="h-sans text-2xl" style={{letterSpacing:"0.2em", fontWeight:600, color:"var(--terracotta)"}} data-testid="invite-code-display">{couple.invite_code}</div>
          </div>
        </div>
        {!both && <div className="h4u-banner mt-4" data-testid="awaiting-partner">Share this 6-character code with your partner so they can sign up and join. Each person answers privately.</div>}
        {!both && (
          <div className="mt-4 p-4 rounded-xl" style={{background:"#f7f3ec", border:"1px solid var(--line)"}} data-testid="invite-email-panel">
            <div className="h-sans" style={{fontWeight:600}}>Or send them the invite by email</div>
            <p className="h-sans mt-1" style={{color:"var(--ink-soft)", lineHeight:1.55, fontSize:14}}>
              We'll email your partner an invite with your 6-character code and a short explanation of how Hope4US works.
            </p>
            <div className="mt-3 grid gap-2 md:grid-cols-2">
              <input
                type="email"
                className="h4u-input"
                placeholder="partner@example.com"
                value={inviteEmail}
                onChange={e=>setInviteEmail(e.target.value)}
                data-testid="invite-email-input"
              />
              <input
                type="text"
                className="h4u-input"
                placeholder="Optional note (one line)"
                value={inviteNote}
                onChange={e=>setInviteNote(e.target.value)}
                maxLength={200}
                data-testid="invite-note-input"
              />
            </div>
            <button
              onClick={sendInviteEmail}
              disabled={!inviteEmail || inviteBusy}
              className="h4u-btn h4u-btn-primary mt-3"
              data-testid="send-invite-email-btn"
            >
              {inviteBusy ? "Sending…" : "Email invite to partner"}
            </button>
            {inviteMsg && (
              <div className="h-sans mt-3 text-sm" style={{color: inviteOk ? "var(--terracotta)" : "var(--muted)"}} data-testid="invite-email-result">
                {inviteMsg}
              </div>
            )}
          </div>
        )}
        {state && (
          <>
            <div className="mt-5">
              <div className="flex justify-between h-sans text-sm" style={{color:"var(--muted)"}}><span>Your intake</span><span>{state.answered_count} / {state.total}</span></div>
              <div className="h4u-progress mt-2"><div style={{width: `${(state.answered_count/state.total)*100}%`}} /></div>
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              {!youDone && <button onClick={()=>navigate("/intake")} className="h4u-btn h4u-btn-primary" data-testid="continue-intake">{state.answered_count===0?"Start my intake":"Continue my intake"}</button>}
              {youDone && both && <button onClick={()=>navigate("/report")} className="h4u-btn h4u-btn-terra" data-testid="view-report">View report</button>}
              {youDone && !both && <div className="h4u-chip">All done — waiting on partner</div>}
            </div>

            {/* Nudge partner CTA — visible only when caller finished & partner hasn't (24h eligibility handled server-side) */}
            {youDone && both && nudgeStatus && !nudgeStatus.eligible && nudgeStatus.reason === "self_not_complete" ? null : null}
            {youDone && both && nudgeStatus && nudgeStatus.reason !== "partner_complete" && (
              <div className="mt-5 p-4 rounded-xl" style={{background:"#f7f3ec", border:"1px solid var(--line)"}} data-testid="nudge-panel">
                <div className="h-sans" style={{fontWeight:600}}>Partner hasn't finished yet?</div>
                <p className="h-sans mt-1" style={{color:"var(--ink-soft)", lineHeight:1.55, fontSize:14}}>
                  {nudgeStatus.eligible
                    ? "You finished your part more than a day ago. Send a gentle nudge — they'll get a note that you've done your side and they're the last piece."
                    : nudgeStatus.reason === "cooldown"
                      ? "You've already sent a nudge in the last 24 hours. We'll let you send another after a full day has passed."
                      : nudgeStatus.hours_since_you_finished != null
                        ? `You finished ${nudgeStatus.hours_since_you_finished.toFixed(1)} hours ago. The nudge unlocks after 24 hours so it doesn't feel rushed.`
                        : "Finish your own intake first, then you can nudge your partner if they take more than a day."}
                </p>
                <button
                  onClick={sendNudge}
                  disabled={!nudgeStatus.eligible || nudgeBusy}
                  className="h4u-btn h4u-btn-primary mt-3"
                  data-testid="send-nudge-btn"
                >
                  {nudgeBusy ? "Sending…" : "Send a gentle nudge"}
                </button>
                {nudgeMsg && <div className="h-sans mt-3 text-sm" style={{color:"var(--muted)"}} data-testid="nudge-result">{nudgeMsg}</div>}
              </div>
            )}
          </>
        )}
      </div>
      <NotificationsBanner />
      <DashboardProgress />
    </div>
  );
}

function NotificationsBanner() {
  const { authHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    try { const r = await apiGet("/notifications/mine", authHeaders()); setItems(r.data.notifications || []); } catch (e) { console.debug("[hope4us:swallowed]", e); }
  }, [authHeaders]);
  useEffect(() => { load(); }, [load]);
  const dismiss = async (id) => {
    try { await apiPost("/notifications/read", { nudge_id: id }, authHeaders()); load(); } catch (e) { console.debug("[hope4us:swallowed]", e); }
  };
  if (!items.length) return null;
  return (
    <div className="mt-4 space-y-3" data-testid="notifications-banner">
      {items.map(n => (
        <div key={n.nudge_id} className="h4u-card p-5 flex items-start justify-between gap-3" style={{background:"#fff5e8", border:"1px solid #e9d3b2"}} data-testid={`notification-${n.nudge_id}`}>
          <div>
            <div className="h-sans" style={{color:"var(--muted)", fontSize:12, textTransform:"uppercase", letterSpacing:"0.08em"}}>A gentle nudge from your partner</div>
            <div className="h-sans mt-1" style={{color:"var(--ink)", lineHeight:1.55}}>{n.reason}</div>
          </div>
          <button onClick={()=>dismiss(n.nudge_id)} className="h4u-btn h4u-btn-ghost" style={{padding:"6px 12px"}} data-testid={`dismiss-notif-${n.nudge_id}`}>Got it ✕</button>
        </div>
      ))}
    </div>
  );
}

function DashboardProgress() {
  const { authHeaders } = useAuth();
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    (async () => {
      try { const r = await apiGet("/dashboard/summary", authHeaders()); setData(r.data); } catch (e) { console.debug("[hope4us:swallowed]", e); }
    })();
  }, [authHeaders]);
  if (!data || !data.has_couple) return null;
  const hasProgress = (data.top_dimensions || []).some(d => (d.score || 0) > 0);
  const phaseLabel = { early: "Getting started", foundation: "Foundation", improving: "Improving", maintenance: "Maintenance" }[data.phase] || data.phase;

  return (
    <div className="h4u-card p-7 mt-5 fade-in" data-testid="dashboard-progress">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="h-sans" style={{color:"var(--muted)", textTransform:"uppercase", letterSpacing:"0.08em", fontSize:12}}>Your growth · {phaseLabel}</div>
          <div className="text-2xl mt-1" style={{fontWeight:500}}>Progress at a glance</div>
        </div>
        <button className="h4u-btn h4u-btn-ghost" onClick={()=>navigate("/progress")} data-testid="view-full-progress">View full progress →</button>
      </div>

      {data.active_practice && (
        <div className="mt-5 p-4 rounded-xl" style={{background:"#fff5e8", border:"1px solid #e9d3b2"}} data-testid="active-practice-card">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <div>
              <div className="h-sans" style={{color:"var(--muted)", fontSize:12, textTransform:"uppercase", letterSpacing:"0.08em"}}>Active practice · {data.active_practice.practice_type === "solo" ? "Do alone" : "Do together, live"}</div>
              <div className="h-sans mt-1" style={{fontWeight:600, fontSize:16}}>{data.active_practice.title}</div>
              <div className="h-sans mt-1" style={{color:"var(--muted)", fontSize:13}}>Step {Math.min((data.active_practice.my_step_index || 0) + 1, data.active_practice.total_steps || 1)} of {data.active_practice.total_steps}</div>
            </div>
            <button className="h4u-btn h4u-btn-primary" onClick={()=>navigate(`/practice/${data.active_practice.practice_id}`)} data-testid="resume-practice">{data.active_practice.my_completed ? "See reflection" : (data.active_practice.my_step_index ? "Resume session" : "Start session")}</button>
          </div>
        </div>
      )}

      {hasProgress ? (
        <>
          <div className="h-sans mt-5" style={{color:"var(--muted)", fontSize:12, textTransform:"uppercase", letterSpacing:"0.08em"}}>Where you're strongest</div>
          <div className="grid md:grid-cols-3 gap-3 mt-2">
            {(data.top_dimensions || []).map((d) => {
              const pct = Math.round((d.score || 0) * 100);
              return (
                <div key={d.dimension} className="p-3 rounded-xl" style={{background:"#f3efe3", border:"1px solid var(--line)"}} data-testid={`dash-dim-${d.dimension}`}>
                  <div className="h-sans" style={{fontWeight:600, fontSize:14}}>{DIMENSION_LABELS[d.dimension] || d.dimension}</div>
                  <div className="h4u-progress mt-2"><div style={{width:`${pct}%`}} /></div>
                  <div className="h-sans mt-1" style={{color:"var(--muted)", fontSize:12}}>{pct}%</div>
                </div>
              );
            })}
          </div>
          {data.focus_areas && data.focus_areas.length > 0 && (
            <div className="mt-5">
              <div className="h-sans" style={{color:"var(--muted)", fontSize:12, textTransform:"uppercase", letterSpacing:"0.08em"}}>Areas that could use gentle attention</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {data.focus_areas.map(d => (
                  <div key={d.dimension} className="h4u-chip" data-testid={`dash-focus-${d.dimension}`}>{DIMENSION_LABELS[d.dimension] || d.dimension} · {Math.round((d.score || 0) * 100)}%</div>
                ))}
              </div>
            </div>
          )}
          {data.latest_milestone && (
            <div className="mt-5 p-3 rounded-xl" style={{background:"#eae7d8", border:"1px solid var(--line)"}} data-testid="latest-milestone">
              <div className="h-sans" style={{color:"var(--muted)", fontSize:12, textTransform:"uppercase", letterSpacing:"0.08em"}}>Latest milestone</div>
              <div className="h-sans mt-1" style={{fontWeight:600}}>✓ {data.latest_milestone.label}</div>
            </div>
          )}
        </>
      ) : (
        <div className="mt-4 h-sans" style={{color:"var(--ink-soft)", lineHeight:1.6}}>Complete a practice to start populating your progress map across the 9 dimensions.</div>
      )}
    </div>
  );
}

// =============== Intake ===============
function Intake() {
  const { authHeaders } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [answer, setAnswer] = useState("");
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [clarification, setClarification] = useState("");
  const [round, setRound] = useState(0);

  const load = useCallback(async () => {
    const r = await apiGet("/intake/state", authHeaders());
    setState(r.data);
    setAnswer(""); setChoice(""); setClarification(""); setRound(0); setErr("");
    if (r.data.complete) {} // stay; user can navigate via Done CTA
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!state?.next_question) return;
    setBusy(true); setErr("");
    const q = state.next_question;
    const value = q.type === "choice" ? choice : answer;
    if (!value || !value.trim()) { setBusy(false); return setErr("Please answer before continuing."); }
    try {
      const r = await apiPost("/intake/answer", { question_id: q.id, answer: value, clarification_round: round }, authHeaders());
      if (r.data.need_clarification) {
        setClarification(r.data.clarification);
        setRound(1);
      } else if (r.data.complete) {
        await load();
      } else {
        // advance: replace state.next_question optimistically
        setState({ ...state, next_question: r.data.next_question, phase: r.data.next_phase, answered_count: state.answered_count + 1 });
        setAnswer(""); setChoice(""); setClarification(""); setRound(0);
      }
    } catch (e) { setErr(e?.response?.data?.detail || "Could not save."); }
    finally { setBusy(false); }
  };

  if (!state) return <Shell><div className="h4u-card p-7 mt-10">Loading…</div></Shell>;
  if (state.complete) return <Shell><div className="h4u-card p-7 mt-10">
    <div className="text-2xl" style={{fontWeight:500}}>You've finished your intake</div>
    <p className="h-sans mt-3" style={{color:"var(--ink-soft)"}}>Once your partner finishes, you can generate the report.</p>
    <button className="h4u-btn h4u-btn-primary mt-4" onClick={()=>navigate("/report")} data-testid="goto-report">Go to report</button>
  </div></Shell>;

  const q = state.next_question;
  const phase = state.phase || {};
  const pct = (state.answered_count / state.total) * 100;

  return (
    <Shell>
      <div className="max-w-2xl mx-auto mt-6">
        <div className="flex justify-between h-sans text-sm" style={{color:"var(--muted)"}}><span>Phase {phase.phase} · {phase.title}</span><span>{state.answered_count + 1} / {state.total}</span></div>
        <div className="h4u-progress mt-2"><div style={{width: `${pct}%`}} /></div>
      </div>

      <div className="max-w-2xl mx-auto h4u-card p-8 mt-6 fade-in">
        {phase.subtitle && <div className="h-sans mb-3" style={{color:"var(--muted)"}}>{phase.subtitle}</div>}
        <div className="text-2xl" style={{fontWeight:500, lineHeight:1.3}} data-testid="question-text">{q.text}</div>
        {q.help && <div className="h4u-banner safe mt-3" data-testid="question-help">{q.help}</div>}

        {q.type === "choice" ? (
          <div className="mt-5 space-y-2">
            {q.choices.map((c, i) => (
              <label key={i} className={`choice-row ${choice===c?"selected":""}`} data-testid={`choice-${i}`}>
                <input type="radio" name="c" className="mr-2" checked={choice===c} onChange={()=>setChoice(c)} />
                {c}
              </label>
            ))}
          </div>
        ) : (
          <div className="mt-5">
            <div className="h-sans" style={{color:"var(--muted)", fontSize:12, textTransform:"uppercase", letterSpacing:"0.08em"}} data-testid="current-input-label">Current Input</div>
            <textarea className="h4u-textarea mt-1" placeholder={(phase.phase ?? 0) === 0 ? "" : "Take your time. Specific is better than perfect."} value={answer} onChange={e=>setAnswer(e.target.value)} data-testid="answer-input" />
          </div>
        )}

        {clarification && <div className="h4u-banner mt-4" data-testid="clarification-banner">{clarification}</div>}
        {err && <div className="h4u-banner danger mt-3">{err}</div>}

        <div className="mt-5 flex justify-end items-center">
          <button onClick={submit} disabled={busy} className="h4u-btn h4u-btn-primary" data-testid="answer-submit">{busy ? "Saving…" : "Continue"}</button>
        </div>
      </div>
    </Shell>
  );
}

// =============== Report ===============
function ReportView() {
  const { authHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try { const r = await apiGet("/report", authHeaders()); setData(r.data); } catch (e) { setErr(e?.response?.data?.detail || "Could not load."); }
  }, [authHeaders]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true); setErr("");
    try { await apiPost("/report/generate", {}, authHeaders()); await load(); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not generate (both partners must complete the current-tier intake first)."); }
    finally { setBusy(false); }
  };

  if (!data) return <Shell><div className="h4u-card p-7 mt-10">Loading…</div></Shell>;

  const tier = data.tier || "free";
  const r = tier === "premium" ? data.report_premium : tier === "plus" ? data.report_plus : data.report_free;

  return (
    <Shell>
      <div className="mt-6 flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="h-sans" style={{color:"var(--muted)", textTransform:"uppercase", letterSpacing:"0.08em", fontSize:12}}>Couple report · {tier} tier</div>
          <h2 className="text-3xl" style={{fontWeight:500}}>{r ? "Your report" : "Generate your report"}</h2>
        </div>
        <div className="flex gap-2">
          <button onClick={generate} disabled={busy} className="h4u-btn h4u-btn-primary" data-testid="generate-report">{busy?"Analyzing…":(r ? "Regenerate" : "Generate report")}</button>
          {tier !== "premium" && <button onClick={()=>navigate("/upgrade")} className="h4u-btn h4u-btn-terra" data-testid="upgrade-cta">Upgrade for deeper insight</button>}
        </div>
      </div>
      {err && <div className="h4u-banner danger mt-4">{err}</div>}

      {!r && (
        <div className="h4u-card p-7 mt-6">
          <p className="h-sans" style={{color:"var(--ink-soft)"}}>Once both partners finish their {tier === "free" ? "free 28-question" : tier === "plus" ? "52-question" : "full 82-question"} intake, click "Generate report" above.</p>
        </div>
      )}

      {r && tier === "free" && <FreeReport r={r} onUpgrade={()=>navigate("/upgrade")} />}
      {r && tier === "plus" && <PlusReport r={r} onUpgrade={()=>navigate("/upgrade")} />}
      {r && tier === "premium" && <PremiumReport r={r} />}
      {data.private_report && <PrivateReportSection r={data.private_report} />}
    </Shell>
  );
}

function PrivateReportSection({ r }) {
  return (<div className="mt-10 fade-in">
    <div className="h4u-chip" style={{background:"var(--plum)", color:"#fffdf7", border:"none"}}>For your eyes only — your private report</div>
    <h2 className="text-3xl mt-3" style={{fontWeight:500}}>Your private insights</h2>
    <p className="h-sans mt-2 max-w-2xl" style={{color:"var(--ink-soft)", lineHeight:1.6}}>This part of the report is only visible to you. Your partner has their own private version. Nothing they wrote appears below.</p>

    {r.safety_note && <SafetyBanner text={r.safety_note} testId="private-safety" />}

    <div className="h4u-card p-7 mt-5">
      <h3 className="text-xl" style={{fontWeight:500}}>How you tend to show up in this dynamic</h3>
      <p className="h-sans mt-2" style={{color:"var(--ink-soft)", lineHeight:1.65}}>{r.summary}</p>
    </div>
    <div className="grid md:grid-cols-2 gap-5 mt-5">
      <div className="h4u-card p-7"><Section title="Personal patterns" items={r.personal_patterns} /></div>
      <div className="h4u-card p-7"><Section title="Communication habits worth noticing" items={r.communication_habits} /></div>
      <div className="h4u-card p-7"><Section title="What tends to activate stress" items={r.emotional_triggers} /></div>
      <div className="h4u-card p-7"><Section title="Personal strengths" items={r.personal_strengths} /></div>
      <div className="h4u-card p-7"><Section title="Blind spots (gentle)" items={r.blind_spots} /></div>
      <div className="h4u-card p-7"><Section title="Growth recommendations" items={r.growth_recommendations} /></div>
    </div>
    {r.individualized_practices && r.individualized_practices.length > 0 && <div className="h4u-card p-7 mt-5">
      <h3 className="text-xl" style={{fontWeight:500}}>Practices just for you</h3>
      <div className="grid md:grid-cols-2 gap-4 mt-3">
        {r.individualized_practices.map((p,i)=>(<div key={i} className="p-4 rounded-xl" style={{background:"#f3efe3", border:"1px solid var(--line)"}}>
          <div className="h-sans" style={{fontWeight:600}}>{p.title}</div>
          <p className="mt-2 h-sans" style={{color:"var(--ink-soft)", lineHeight:1.6}}>{p.description}</p>
        </div>))}
      </div>
    </div>}
    {r.questions_to_journal && r.questions_to_journal.length > 0 && <div className="h4u-card p-7 mt-5">
      <Section title="Questions to journal on" items={r.questions_to_journal} />
    </div>}
  </div>);
}

function FreeReport({ r, onUpgrade }) {
  return (<div className="space-y-5 mt-6 fade-in">
    {r.safety_note && <SafetyBanner text={r.safety_note} testId="safety-note" />}
    <div className="h4u-card p-7">
      <h3 className="text-xl" style={{fontWeight:500}}>The dynamic at a glance</h3>
      <p className="h-sans mt-2" style={{color:"var(--ink-soft)", lineHeight:1.65}}>{r.summary}</p>
    </div>
    <div className="h4u-card p-7">
      <div className="h4u-chip">An interaction loop we noticed</div>
      <p className="h-sans mt-3" style={{color:"var(--ink-soft)", lineHeight:1.65}}>{r.interaction_loop}</p>
    </div>
    <div className="grid md:grid-cols-2 gap-5">
      <div className="h4u-card p-7"><div className="h4u-chip">A strength of this dynamic</div><p className="h-sans mt-3" style={{color:"var(--ink-soft)", lineHeight:1.6}}>{r.strength_pattern}</p></div>
      <div className="h4u-card p-7"><div className="h4u-chip">An area for growth</div><p className="h-sans mt-3" style={{color:"var(--ink-soft)", lineHeight:1.6}}>{r.growth_pattern}</p></div>
    </div>
    <div className="h4u-card p-7">
      <h3 className="text-xl" style={{fontWeight:500}}>Communication under stress</h3>
      <p className="h-sans mt-2" style={{color:"var(--ink-soft)", lineHeight:1.65}}>{r.communication_dynamic_note}</p>
    </div>
    <div className="h4u-card p-7">
      <h3 className="text-xl" style={{fontWeight:500}}>How care moves through this relationship</h3>
      <p className="h-sans mt-2" style={{color:"var(--ink-soft)", lineHeight:1.65}}>{r.love_expression_note}</p>
    </div>
    {r.optional_practice && <div className="h4u-card p-7">
      <div className="h4u-chip">An optional practice</div>
      <div className="h-sans mt-3" style={{fontWeight:600}}>{r.optional_practice.title}</div>
      <p className="h-sans mt-1" style={{color:"var(--ink-soft)", lineHeight:1.6}}>{r.optional_practice.description}</p>
    </div>}
    {r.question_to_explore && <div className="h4u-card p-7">
      <div className="h4u-chip">A question to explore together</div>
      <p className="h-sans mt-3" style={{color:"var(--ink-soft)", lineHeight:1.6}}>{r.question_to_explore}</p>
    </div>}
    <div className="h4u-banner" data-testid="upgrade-banner">
      <div style={{fontWeight:600}}>Want a deeper look?</div>
      <div className="mt-1">Plus ($19.99/mo for the couple) goes ~52 questions deep into interaction patterns, communication dynamics, and emotional cycles. Premium ($29.99/mo for the couple) is the full 82-question dive with ongoing pattern tracking, adaptive practices, and updated insights as the relationship evolves. Either partner subscribes — both get access. Cancel anytime.</div>
      <button onClick={onUpgrade} className="h4u-btn h4u-btn-terra mt-3">Compare tiers</button>
    </div>
  </div>);
}

function PlusReport({ r, onUpgrade }) {
  return (<div className="space-y-5 mt-6 fade-in">
    {r.safety_note && <SafetyBanner text={r.safety_note} testId="safety-note-plus" />}
    <div className="h4u-card p-7">
      <h3 className="text-xl" style={{fontWeight:500}}>The dynamic at a glance</h3>
      <p className="h-sans mt-2" style={{color:"var(--ink-soft)", lineHeight:1.65}}>{r.summary}</p>
      {r.detailed_dynamic && <p className="h-sans mt-3" style={{color:"var(--ink-soft)", lineHeight:1.65}}>{r.detailed_dynamic}</p>}
    </div>
    <div className="grid md:grid-cols-2 gap-5">
      <div className="h4u-card p-7"><Section title="Interaction loops" items={r.interaction_loops} /></div>
      <div className="h4u-card p-7"><Section title="Emotional cycles" items={r.emotional_cycles} /></div>
      <div className="h4u-card p-7"><Section title="Strength patterns" items={r.strength_patterns} /></div>
      <div className="h4u-card p-7"><Section title="Growth patterns" items={r.growth_patterns} /></div>
      <div className="h4u-card p-7"><Section title="Alignment areas" items={r.alignment_areas} /></div>
      <div className="h4u-card p-7"><Section title="Friction areas" items={r.friction_areas} /></div>
    </div>
    <div className="h4u-card p-7">
      <h3 className="text-xl" style={{fontWeight:500}}>Communication dynamics</h3>
      <p className="h-sans mt-2" style={{color:"var(--ink-soft)", lineHeight:1.65}}>{r.communication_dynamics}</p>
    </div>
    <div className="h4u-card p-7">
      <h3 className="text-xl" style={{fontWeight:500}}>How care moves through this relationship</h3>
      <p className="h-sans mt-2" style={{color:"var(--ink-soft)", lineHeight:1.65}}>{r.love_expression_dynamics}</p>
    </div>
    <div className="h4u-card p-7">
      <h3 className="text-xl" style={{fontWeight:500}}>Compatibility across dimensions</h3>
      <div className="grid md:grid-cols-2 gap-3 mt-3 h-sans" style={{color:"var(--ink-soft)"}}>
        {Object.entries(r.compatibility_dimensions || {}).map(([k,v])=>(<div key={k}><strong className="capitalize">{k.replace(/_/g," ")}:</strong> {v}</div>))}
      </div>
    </div>
    <div className="grid md:grid-cols-2 gap-5">
      <div className="h4u-card p-7"><Section title="What can intensify these loops" items={r.what_can_intensify_loops} /></div>
      <div className="h4u-card p-7"><Section title="Questions to explore together" items={r.questions_to_explore} /></div>
    </div>
    {r.optional_practices && r.optional_practices.length > 0 && <div className="h4u-card p-7">
      <h3 className="text-xl" style={{fontWeight:500}}>Optional practices</h3>
      <div className="grid md:grid-cols-2 gap-4 mt-3">
        {r.optional_practices.map((p,i)=>(<div key={i} className="p-4 rounded-xl" style={{background:"#f3efe3", border:"1px solid var(--line)"}}>
          <div className="h-sans" style={{fontWeight:600}}>{p.title}</div>
          <p className="mt-2 h-sans" style={{color:"var(--ink-soft)", lineHeight:1.6}}>{p.description}</p>
        </div>))}
      </div>
    </div>}
    {r.communication_structures && r.communication_structures.length > 0 && <div className="h4u-card p-7">
      <h3 className="text-xl" style={{fontWeight:500}}>Communication structures to try</h3>
      <div className="grid md:grid-cols-2 gap-4 mt-3">
        {r.communication_structures.map((s,i)=>(<div key={i} className="p-4 rounded-xl" style={{background:"#f3efe3", border:"1px solid var(--line)"}}>
          <div className="h-sans" style={{fontWeight:600}}>{s.title}</div>
          <p className="mt-2 h-sans" style={{color:"var(--ink-soft)", lineHeight:1.55}}>{s.structure}</p>
          <p className="mt-2 h-sans" style={{color:"var(--ink-soft)", lineHeight:1.55, fontStyle:"italic"}}>"{s.example_phrasing}"</p>
        </div>))}
      </div>
    </div>}
    <div className="h4u-banner">
      <div style={{fontWeight:600}}>Go deeper with Premium ($29.99/mo · for the couple)</div>
      <div className="mt-1">Full 82-question intake with ongoing pattern tracking, expanded relational dynamics analysis, premarital themes, and personalized practice plans that adapt as the relationship grows. Either partner subscribes — both get access. Cancel anytime.</div>
      <button onClick={onUpgrade} className="h4u-btn h4u-btn-terra mt-3">Upgrade to Premium</button>
    </div>
  </div>);
}

function Section({ title, items, className }) {
  if (!items || items.length === 0) return null;
  return (
    <div className={className||""}>
      <div className="h-sans" style={{color:"var(--muted)", textTransform:"uppercase", letterSpacing:"0.08em", fontSize:12}}>{title}</div>
      <ul className="h-sans mt-2 space-y-2" style={{color:"var(--ink-soft)", lineHeight:1.6}}>
        {items.map((x,i)=><li key={i} className="flex gap-2"><span style={{color:"var(--terracotta)"}}>·</span><span>{typeof x === "string" ? x : JSON.stringify(x)}</span></li>)}
      </ul>
    </div>
  );
}

function PremiumReport({ r }) {
  return (
    <div className="space-y-5 mt-6 fade-in">
      {r.safety_note && <SafetyBanner text={r.safety_note} testId="safety-note-premium" />}
      <div className="h4u-card p-7">
        <h3 className="text-xl" style={{fontWeight:500}}>The dynamic at a glance</h3>
        <p className="h-sans mt-2" style={{color:"var(--ink-soft)", lineHeight:1.65}}>{r.summary}</p>
        {r.detailed_dynamic && <p className="h-sans mt-3" style={{color:"var(--ink-soft)", lineHeight:1.65}}>{r.detailed_dynamic}</p>}
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <div className="h4u-card p-7"><Section title="Interaction loops" items={r.interaction_loops} /></div>
        <div className="h4u-card p-7"><Section title="Emotional cycles" items={r.emotional_cycles} /></div>
        <div className="h4u-card p-7"><Section title="Strength patterns" items={r.strength_patterns} /></div>
        <div className="h4u-card p-7"><Section title="Growth patterns" items={r.growth_patterns} /></div>
        <div className="h4u-card p-7"><Section title="Alignment areas" items={r.alignment_areas} /></div>
        <div className="h4u-card p-7"><Section title="Friction areas" items={r.friction_areas} /></div>
        <div className="h4u-card p-7"><Section title="Protective dynamics" items={r.protective_dynamics} /></div>
        <div className="h4u-card p-7"><Section title="Destabilizing dynamics" items={r.destabilizing_dynamics} /></div>
      </div>
      <div className="h4u-card p-7">
        <h3 className="text-xl" style={{fontWeight:500}}>Communication dynamics</h3>
        <p className="h-sans mt-2" style={{color:"var(--ink-soft)", lineHeight:1.65}}>{r.communication_dynamics}</p>
      </div>
      <div className="h4u-card p-7">
        <h3 className="text-xl" style={{fontWeight:500}}>How care moves through this relationship</h3>
        <p className="h-sans mt-2" style={{color:"var(--ink-soft)", lineHeight:1.65}}>{r.love_expression_dynamics}</p>
      </div>
      <div className="h4u-card p-7">
        <h3 className="text-xl" style={{fontWeight:500}}>Compatibility across dimensions</h3>
        <div className="grid md:grid-cols-2 gap-3 mt-3 h-sans" style={{color:"var(--ink-soft)"}}>
          {Object.entries(r.compatibility_dimensions || {}).map(([k,v])=>(<div key={k}><strong className="capitalize">{k.replace(/_/g," ")}:</strong> {v}</div>))}
        </div>
      </div>
      {r.premarital_themes && <div className="h4u-card p-7">
        <h3 className="text-xl" style={{fontWeight:500}}>Premarital / commitment themes</h3>
        <div className="grid md:grid-cols-2 gap-3 mt-3 h-sans" style={{color:"var(--ink-soft)"}}>
          {Object.entries(r.premarital_themes).filter(([k])=>k!=="areas_for_pre_commitment_dialogue").map(([k,v])=>(<div key={k}><strong className="capitalize">{k.replace(/_/g," ")}:</strong> {v}</div>))}
        </div>
        {r.premarital_themes.areas_for_pre_commitment_dialogue && <Section title="Areas worth exploring before commitment" items={r.premarital_themes.areas_for_pre_commitment_dialogue} className="mt-4" />}
      </div>}
      {r.optional_practices && r.optional_practices.length > 0 && <div className="h4u-card p-7">
        <h3 className="text-xl" style={{fontWeight:500}}>Optional practices</h3>
        <div className="grid md:grid-cols-2 gap-4 mt-3">
          {r.optional_practices.map((p,i)=>(<div key={i} className="p-4 rounded-xl" style={{background:"#f3efe3", border:"1px solid var(--line)"}}>
            <div className="h-sans" style={{fontWeight:600}}>{p.title}</div>
            <p className="mt-2 h-sans" style={{color:"var(--ink-soft)", lineHeight:1.6}}>{p.description}</p>
          </div>))}
        </div>
      </div>}
      {r.communication_structures && r.communication_structures.length > 0 && <div className="h4u-card p-7">
        <h3 className="text-xl" style={{fontWeight:500}}>Communication structures to try</h3>
        <div className="grid md:grid-cols-2 gap-4 mt-3">
          {r.communication_structures.map((s,i)=>(<div key={i} className="p-4 rounded-xl" style={{background:"#f3efe3", border:"1px solid var(--line)"}}>
            <div className="h-sans" style={{fontWeight:600}}>{s.title}</div>
            <p className="mt-2 h-sans" style={{color:"var(--ink-soft)", lineHeight:1.55}}>{s.structure}</p>
            <p className="mt-2 h-sans" style={{color:"var(--ink-soft)", lineHeight:1.55, fontStyle:"italic"}}>"{s.example_phrasing}"</p>
          </div>))}
        </div>
      </div>}
      <div className="grid md:grid-cols-2 gap-5">
        <div className="h4u-card p-7"><Section title="Weekly practices" items={r.weekly_practices} /></div>
        <div className="h4u-card p-7"><Section title="Monthly practices" items={r.monthly_practices} /></div>
        <div className="h4u-card p-7"><Section title="Questions to explore together" items={r.questions_to_explore} /></div>
        <div className="h4u-card p-7"><Section title="What can intensify these loops" items={r.what_can_intensify_loops} /></div>
      </div>
      <div className="h4u-card p-7"><Section title="What more information would clarify" items={r.what_data_would_clarify} /></div>
      <div className="h4u-card p-7"><Section title="When extra support could help" items={r.when_extra_support_could_help} /></div>
    </div>
  );
}

// =============== Progress (Sections 6/8: adaptive memory + milestones + regressions) ===============
const DIMENSION_LABELS = {
  communication: "Communication", conflict_recovery: "Conflict recovery",
  emotional_regulation: "Emotional regulation", trust: "Trust", connection: "Connection",
  understanding: "Understanding", repair_ability: "Repair ability",
  follow_through: "Follow-through", bond: "Bond",
};
const PHASE_LABELS = { early: "Getting started", foundation: "Foundation", improving: "Improving", maintenance: "Maintenance (graduated)" };

function Progress() {
  const { authHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { (async()=>{ try { const r = await apiGet("/progress", authHeaders()); setData(r.data); } catch (e) { setErr(e?.response?.data?.detail || "Could not load."); } })(); }, [authHeaders]);
  if (err) return <Shell><div className="h4u-banner danger mt-8">{err}</div></Shell>;
  if (!data) return <Shell><div className="h4u-card p-7 mt-10">Loading…</div></Shell>;
  const achievedIds = new Set((data.milestones || []).map(m => m.id));
  return (<Shell>
    <div className="mt-6 flex items-baseline justify-between flex-wrap gap-3">
      <div>
        <div className="h-sans" style={{color:"var(--muted)", textTransform:"uppercase", letterSpacing:"0.08em", fontSize:12}}>Progress · {PHASE_LABELS[data.phase] || data.phase}</div>
        <h2 className="text-3xl" style={{fontWeight:500}}>How your relationship is evolving</h2>
        <p className="h-sans mt-2 max-w-2xl" style={{color:"var(--ink-soft)", lineHeight:1.6}}>Every practice you complete gently nudges these dimensions. Growth is measured by improvement, not perfection.</p>
      </div>
      {data.graduated && <div className="h4u-chip" style={{background:"var(--sage-2)", color:"#fffdf7", border:"none"}}>Maintenance phase 🌱</div>}
    </div>

    {data.regressions && data.regressions.length > 0 && <div className="h4u-banner mt-6" data-testid="regressions-banner">
      <div style={{fontWeight:600}}>Areas that could use a revisit</div>
      <ul className="mt-2 space-y-1">{data.regressions.map((r,i)=>(<li key={i} className="h-sans" style={{fontSize:14}}>· <strong>{DIMENSION_LABELS[r.dimension] || r.dimension}</strong> — {r.suggestion}</li>))}</ul>
    </div>}

    <div className="h4u-card p-7 mt-6">
      <h3 className="text-xl" style={{fontWeight:500}}>Dimensions</h3>
      <div className="grid md:grid-cols-3 gap-4 mt-4">
        {Object.entries(data.dimensions || {}).map(([k, s]) => {
          const pct = Math.round((s.score || 0) * 100);
          return (<div key={k} className="p-4 rounded-xl" style={{background:"#f3efe3", border:"1px solid var(--line)"}} data-testid={`dim-${k}`}>
            <div className="h-sans" style={{fontWeight:600}}>{DIMENSION_LABELS[k] || k}</div>
            <div className="h4u-progress mt-2"><div style={{width:`${pct}%`}} /></div>
            <div className="h-sans mt-2" style={{color:"var(--muted)", fontSize:12}}>{pct}% · {(s.history || []).length} data points</div>
          </div>);
        })}
      </div>
    </div>

    <div className="h4u-card p-7 mt-6">
      <h3 className="text-xl" style={{fontWeight:500}}>Milestones</h3>
      <div className="grid md:grid-cols-2 gap-3 mt-4">
        {(data.milestones_available || []).map((m,i) => {
          const done = achievedIds.has(m.id);
          return (<div key={i} className="flex items-center gap-3 p-3 rounded-xl" style={{background: done ? "#eae7d8" : "#f7f3ec", border:"1px solid var(--line)", opacity: done ? 1 : 0.55}} data-testid={`milestone-${m.id}`}>
            <div style={{width:26,height:26,borderRadius:"50%", background: done ? "var(--sage-2)" : "transparent", border:"2px solid var(--sage-2)", display:"flex", alignItems:"center", justifyContent:"center", color:"#fffdf7", fontWeight:600, fontSize:14}}>{done ? "✓" : ""}</div>
            <div className="h-sans" style={{fontWeight: done ? 600 : 500}}>{m.label}</div>
          </div>);
        })}
      </div>
    </div>

    {data.last_updated && <div className="h-sans mt-4 text-sm" style={{color:"var(--muted)"}}>Last updated: {new Date(data.last_updated).toLocaleString()}</div>}
  </Shell>);
}

// =============== Practices (Section 6/7) ===============
const CATEGORY_LABELS = {
  communication: "Communication", emotional_regulation: "Emotional regulation",
  conflict: "Conflict", connection: "Connection", trust: "Trust",
  future_alignment: "Future alignment", bond: "Bond",
};

function Practices() {
  const { authHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [picked, setPicked] = useState("");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try { const r = await apiGet("/practices", authHeaders()); setData(r.data); } catch (e) { setErr(e?.response?.data?.detail || "Could not load."); }
  }, [authHeaders]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true); setErr("");
    try {
      await apiPost("/practices/generate", picked ? { preferred_category: picked } : {}, authHeaders());
      await load();
    } catch (e) { setErr(e?.response?.data?.detail || "Could not generate."); }
    finally { setBusy(false); }
  };

  if (!data) return <Shell><div className="h4u-card p-7 mt-10">Loading…</div></Shell>;

  return (<Shell>
    <div className="mt-6 flex items-baseline justify-between flex-wrap gap-3">
      <div>
        <div className="h-sans" style={{color:"var(--muted)", textTransform:"uppercase", letterSpacing:"0.08em", fontSize:12}}>Personalized practices · {data.tier} tier</div>
        <h2 className="text-3xl" style={{fontWeight:500}}>Try something this week</h2>
      </div>
      <div className="flex gap-2 items-center">
        <select value={picked} onChange={e=>setPicked(e.target.value)} className="h4u-input" style={{padding:"10px 14px", minWidth:200}} data-testid="practice-category">
          <option value="">Any category</option>
          {data.categories.map(c=>(<option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>))}
        </select>
        <button onClick={generate} disabled={busy} className="h4u-btn h4u-btn-primary" data-testid="generate-practice">{busy?"Generating…":"Generate a practice"}</button>
      </div>
    </div>
    {err && <div className="h4u-banner danger mt-4">{err}</div>}
    {data.practices.length === 0 && <div className="h4u-card p-7 mt-6">
      <p className="h-sans" style={{color:"var(--ink-soft)"}}>No practices yet. Generate one above — we'll tailor it to your dynamic. Free gets 1 per week, Plus 3, Premium unlimited.</p>
    </div>}
    <div className="space-y-5 mt-6">
      {data.practices.map(p => <PracticeCard key={p.practice_id} p={p} onChange={load} />)}
    </div>
  </Shell>);
}

function PracticeCard({ p, onChange }) {
  const navigate = useNavigate();
  const totalSteps = (p.steps || []).length;
  const myIdx = p.my_step_index || 0;
  const myDone = !!p.my_completed;
  const isTogether = p.practice_type === "together";
  const isSolo = p.practice_type === "solo";

  const statusLabel = () => {
    if (p.status === "completed" || (p.synthesis && myDone)) return "Session complete";
    if (myDone) return isTogether ? "Waiting for your partner" : "Complete";
    if (myIdx > 0) return `In progress — step ${Math.min(myIdx+1, totalSteps)}/${totalSteps}`;
    return "Ready to begin";
  };

  const cta = () => {
    if (p.synthesis) return "See reflection";
    if (myDone) return "See reflection";
    if (myIdx > 0) return "Resume session";
    return "Start guided session";
  };

  return (<div className="h4u-card p-7 fade-in" data-testid={`practice-${p.practice_id}`}>
    <div className="flex items-baseline justify-between flex-wrap gap-2">
      <div>
        <div className="flex gap-2 flex-wrap">
          <div className="h4u-chip">{CATEGORY_LABELS[p.category] || p.category}</div>
          <div className="h4u-chip" style={{background: isSolo ? "var(--plum)" : "var(--sage-2)", color:"#fffdf7", border:"none"}} data-testid={`practice-type-${p.practice_id}`}>
            {isSolo ? "Do alone" : "Do together · live"}
          </div>
          <div className="h4u-chip">{p.duration_minutes || 10} min · {totalSteps} steps</div>
        </div>
        <h3 className="text-xl mt-3" style={{fontWeight:500}}>{p.title}</h3>
      </div>
      <div className="h-sans text-sm" style={{color:"var(--muted)"}}>{statusLabel()}</div>
    </div>
    {p.rationale && <p className="h-sans mt-3" style={{color:"var(--ink-soft)", lineHeight:1.6, fontStyle:"italic"}}>{p.rationale}</p>}
    {isTogether && !myDone && <div className="h4u-banner mt-4" style={{fontSize:14}} data-testid={`together-hint-${p.practice_id}`}>
      This one is done together, live. You'll each be on your own device, moving through the same steps in sync. Neither of you sees the other's answers — just presence.
    </div>}
    {isSolo && !myDone && <div className="h4u-banner mt-4" style={{fontSize:14}} data-testid={`solo-hint-${p.practice_id}`}>
      Do this one at your own pace, alone. Your partner has their own copy to do independently.
    </div>}
    <div className="mt-5">
      <button className="h4u-btn h4u-btn-primary" onClick={()=>navigate(`/practice/${p.practice_id}`)} data-testid={`open-session-${p.practice_id}`}>{cta()}</button>
    </div>
  </div>);
}

// =============== Practice Session (guided step-by-step, medium-branching, sync-gated for 'together') ===============
function PracticeSession() {
  const { authHeaders } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [response, setResponse] = useState("");
  const [choice, setChoice] = useState("");
  const [moodBefore, setMoodBefore] = useState("");
  const [moodAfter, setMoodAfter] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [nudge, setNudge] = useState(null);
  const [showBreather, setShowBreather] = useState(false);
  const [finishing, setFinishing] = useState(false);

  const load = useCallback(async () => {
    try { const r = await apiGet(`/practices/${id}/session`, authHeaders()); setSession(r.data); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not load session."); }
  }, [authHeaders, id]);

  useEffect(() => { load(); }, [load]);

  // Poll every 3s for "together" practices when waiting on partner or when partner might advance
  useEffect(() => {
    if (!session) return;
    if (session.practice_type !== "together") return;
    if (session.state === "complete") return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [session, load]);

  // When both partners finish steps, auto-trigger finish (idempotent)
  useEffect(() => {
    if (!session) return;
    const allStepsDone = (session.my_step_index || 0) >= (session.steps || []).length;
    if (!allStepsDone) return;
    if (session.synthesis) return;
    if (session.practice_type === "together") {
      const partnerDone = (session.partner_states || []).every(p => p.completed);
      if (!partnerDone) return;
    }
    if (finishing) return;
    setFinishing(true);
    (async () => {
      try { const r = await apiPost(`/practices/${id}/finish`, {}, authHeaders()); setSession(r.data); }
      catch (e) { setErr(e?.response?.data?.detail || "Could not synthesize."); }
      finally { setFinishing(false); }
    })();
  }, [session, id, authHeaders, finishing]);

  if (err) return <Shell><div className="h4u-banner danger mt-8" data-testid="session-error">{err}</div></Shell>;
  if (!session) return <Shell><div className="h4u-card p-7 mt-10">Loading…</div></Shell>;

  const steps = session.steps || [];
  const totalSteps = steps.length;
  const myIdx = session.my_step_index || 0;
  const currentStep = session.current_step;
  const isTogether = session.practice_type === "together";
  const allStepsDone = myIdx >= totalSteps;

  // ---- Synthesis view (session complete) ----
  if (session.synthesis) {
    return <Shell><SynthesisView session={session} onDone={()=>navigate("/practices")} /></Shell>;
  }

  // ---- Waiting-for-partner interstitial (together, submitted current step) ----
  if (session.waiting_for_partner) {
    return <Shell>
      <SessionHeader session={session} myIdx={myIdx} totalSteps={totalSteps} />
      <div className="max-w-2xl mx-auto h4u-card p-8 mt-6 fade-in" data-testid="waiting-partner">
        <div className="h4u-chip" style={{background:"var(--sage-2)", color:"#fffdf7", border:"none"}}>Sync moment</div>
        <h3 className="text-2xl mt-3" style={{fontWeight:500}}>Waiting for your partner…</h3>
        <p className="h-sans mt-3" style={{color:"var(--ink-soft)", lineHeight:1.6}}>You've completed this step. As soon as your partner finishes theirs, you'll both move to the next one together. Take a breath — you don't have to fill this silence.</p>
        <div className="mt-4 h-sans text-sm" style={{color:"var(--muted)"}}>Auto-updating every few seconds.</div>
      </div>
    </Shell>;
  }

  // ---- Auto-synthesizing interstitial ----
  if (allStepsDone && !session.synthesis) {
    return <Shell>
      <SessionHeader session={session} myIdx={totalSteps} totalSteps={totalSteps} />
      <div className="max-w-2xl mx-auto h4u-card p-8 mt-6 fade-in" data-testid="synthesizing">
        <h3 className="text-2xl" style={{fontWeight:500}}>Wrapping up…</h3>
        <p className="h-sans mt-3" style={{color:"var(--ink-soft)", lineHeight:1.6}}>Bringing the pieces of your session together. This takes a few seconds.</p>
      </div>
    </Shell>;
  }

  if (!currentStep) return <Shell><div className="h4u-card p-7 mt-10">Preparing…</div></Shell>;

  const submitStep = async () => {
    const value = currentStep.response_type === "choice" ? choice : response;
    if (!value || !value.trim()) { setErr("Add a brief response before continuing."); return; }
    setBusy(true); setErr("");
    try {
      const r = await apiPost(`/practices/${id}/step`, {
        step_id: currentStep.step_id,
        response: value.trim(),
        mood_before: moodBefore || null,
        mood_after: moodAfter || null,
      }, authHeaders());
      const nud = r.data.adaptive_nudge;
      if (nud && nud.signal === "slow_down") {
        setNudge(nud); setShowBreather(true);
      } else if (nud && nud.nudge) {
        setNudge(nud); setShowBreather(false);
      } else {
        setNudge(null); setShowBreather(false);
      }
      setSession(r.data);
      setResponse(""); setChoice(""); setMoodBefore(""); setMoodAfter("");
    } catch (e) { setErr(e?.response?.data?.detail || "Could not save."); }
    finally { setBusy(false); }
  };

  const closeNudge = () => { setNudge(null); setShowBreather(false); };

  return (<Shell>
    <SessionHeader session={session} myIdx={myIdx} totalSteps={totalSteps} />

    {/* Grounding intro on step 1 */}
    {myIdx === 0 && session.grounding && (
      <div className="max-w-2xl mx-auto h4u-banner mt-5" data-testid="grounding-intro">
        <div style={{fontWeight:600}}>Before we begin</div>
        <p className="h-sans mt-1" style={{lineHeight:1.6}}>{session.grounding}</p>
      </div>
    )}

    {/* Adaptive nudge card — shows after previous step, before this step's textarea */}
    {nudge && nudge.nudge && (
      <div className="max-w-2xl mx-auto h4u-card p-6 mt-5 fade-in" style={{background: showBreather ? "#eae7d8" : "#fff5e8", border:"1px solid var(--line)"}} data-testid="adaptive-nudge">
        <div className="h4u-chip" style={{background: showBreather ? "var(--plum)" : "var(--terracotta)", color:"#fffdf7", border:"none"}}>
          {showBreather ? "Take a breath" : nudge.signal === "encourage" ? "A gentle nudge" : "A small moment"}
        </div>
        <p className="h-sans mt-3" style={{color:"var(--ink-soft)", lineHeight:1.65}}>{nudge.nudge}</p>
        <button className="h4u-btn h4u-btn-ghost mt-4" onClick={closeNudge} data-testid="dismiss-nudge">Continue</button>
      </div>
    )}

    {/* Current step */}
    <div className="max-w-2xl mx-auto h4u-card p-8 mt-6 fade-in" data-testid={`step-${currentStep.step_id}`}>
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div className="h-sans" style={{color:"var(--muted)", textTransform:"uppercase", letterSpacing:"0.08em", fontSize:12}}>Step {myIdx + 1} · {currentStep.title}</div>
        <div className="h-sans text-sm" style={{color:"var(--muted)"}}>~{Math.round((currentStep.estimated_seconds || 60) / 60)} min</div>
      </div>
      <h2 className="text-2xl mt-3" style={{fontWeight:500, lineHeight:1.35}} data-testid="step-prompt">{currentStep.prompt}</h2>
      {currentStep.micro_instruction && (
        <div className="mt-4 p-3 rounded-xl" style={{background:"#f3efe3", border:"1px dashed var(--line)"}} data-testid="micro-instruction">
          <div className="h-sans" style={{fontWeight:600, fontSize:13, textTransform:"uppercase", letterSpacing:"0.08em", color:"var(--muted)"}}>Try this</div>
          <p className="h-sans mt-1" style={{lineHeight:1.55}}>{currentStep.micro_instruction}</p>
        </div>
      )}
      {currentStep.why_this_matters && (
        <p className="h-sans mt-3 text-sm" style={{color:"var(--muted)", fontStyle:"italic"}}>Why this matters: {currentStep.why_this_matters}</p>
      )}

      {/* Response field */}
      <div className="mt-5">
        {currentStep.response_type === "choice" && Array.isArray(currentStep.response_choices) ? (
          <div className="space-y-2">
            {currentStep.response_choices.map((c, i) => (
              <label key={i} className={`choice-row ${choice===c?"selected":""}`} data-testid={`step-choice-${i}`}>
                <input type="radio" name="stepc" className="mr-2" checked={choice===c} onChange={()=>setChoice(c)} />
                {c}
              </label>
            ))}
          </div>
        ) : (
          <textarea className="h4u-textarea" placeholder={currentStep.response_placeholder || "Take your time. A sentence is plenty."} value={response} onChange={e=>setResponse(e.target.value)} data-testid="step-response-input" />
        )}
        {myIdx === 0 && (
          <div className="mt-3">
            <div className="h-sans" style={{color:"var(--muted)", fontSize:12}}>Optional — one word for how you're feeling before this step:</div>
            <input className="h4u-input mt-1" placeholder="e.g., anxious, curious, tired" value={moodBefore} onChange={e=>setMoodBefore(e.target.value)} data-testid="mood-before" />
          </div>
        )}
        {myIdx === totalSteps - 1 && (
          <div className="mt-3">
            <div className="h-sans" style={{color:"var(--muted)", fontSize:12}}>Optional — one word for how you're feeling now:</div>
            <input className="h4u-input mt-1" placeholder="e.g., softer, still guarded, warmer" value={moodAfter} onChange={e=>setMoodAfter(e.target.value)} data-testid="mood-after" />
          </div>
        )}
      </div>

      {err && <div className="h4u-banner danger mt-4">{err}</div>}

      <div className="mt-5 flex justify-between items-center">
        <button className="h4u-btn h4u-btn-ghost" onClick={()=>navigate("/practices")} data-testid="exit-session">Exit</button>
        <button onClick={submitStep} disabled={busy} className="h4u-btn h4u-btn-primary" data-testid="step-submit">{busy?"Saving…":(myIdx === totalSteps - 1 ? "Finish step" : "Save & continue")}</button>
      </div>
    </div>

    {/* Partner presence for together practices */}
    {isTogether && session.partner_states && session.partner_states.length > 0 && (
      <div className="max-w-2xl mx-auto mt-4">
        <div className="h-sans text-sm" style={{color:"var(--muted)"}} data-testid="partner-presence">
          Partner is on step {(session.partner_states[0].step_index || 0) + 1} of {totalSteps}{session.partner_states[0].completed ? " · finished" : ""}
        </div>
      </div>
    )}
  </Shell>);
}

function SessionHeader({ session, myIdx, totalSteps }) {
  const navigate = useNavigate();
  const pct = totalSteps ? (myIdx / totalSteps) * 100 : 0;
  return (
    <div className="max-w-2xl mx-auto mt-6">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="h-sans" style={{color:"var(--muted)", textTransform:"uppercase", letterSpacing:"0.08em", fontSize:12}} data-testid="session-header-meta">
            {(CATEGORY_LABELS[session.category] || session.category)} · {session.practice_type === "solo" ? "Solo practice" : "Together · live"}
          </div>
          <h2 className="text-2xl mt-1" style={{fontWeight:500}} data-testid="session-title">{session.title}</h2>
        </div>
        <button className="h4u-btn h4u-btn-ghost" onClick={()=>navigate("/practices")} data-testid="all-practices">All practices</button>
      </div>
      <div className="flex justify-between h-sans text-sm mt-3" style={{color:"var(--muted)"}}>
        <span>Progress</span><span>{Math.min(myIdx, totalSteps)} / {totalSteps} steps</span>
      </div>
      <div className="h4u-progress mt-2"><div style={{width: `${pct}%`}} /></div>
    </div>
  );
}

function SynthesisView({ session, onDone }) {
  return (
    <div className="max-w-2xl mx-auto mt-8 fade-in" data-testid="synthesis-view">
      <div className="h-sans" style={{color:"var(--muted)", textTransform:"uppercase", letterSpacing:"0.08em", fontSize:12}}>Session complete</div>
      <h2 className="text-3xl mt-1" style={{fontWeight:500}}>What you both moved through</h2>
      <p className="h-sans mt-3" style={{color:"var(--ink-soft)", lineHeight:1.65}}>{session.shared_reflection || "You completed the session. Give it a moment to settle."}</p>

      {session.private_takeaway && (
        <div className="h4u-card p-7 mt-6" style={{border:"1px dashed var(--plum)", background:"#f3efe3"}} data-testid="private-takeaway">
          <div className="h4u-chip" style={{background:"var(--plum)", color:"#fffdf7", border:"none"}}>For your eyes only</div>
          <h3 className="text-xl mt-3" style={{fontWeight:500}}>Your personal takeaway</h3>
          <p className="h-sans mt-2" style={{color:"var(--ink-soft)", lineHeight:1.65}}>{session.private_takeaway}</p>
        </div>
      )}

      {(session.signals_observed || []).length > 0 && (
        <div className="h4u-card p-7 mt-5">
          <div className="h-sans" style={{color:"var(--muted)", fontSize:12, textTransform:"uppercase", letterSpacing:"0.08em"}}>What showed up</div>
          <div className="flex flex-wrap gap-2 mt-2">
            {session.signals_observed.map((s, i) => <div key={i} className="h4u-chip" data-testid={`signal-${i}`}>{s}</div>)}
          </div>
        </div>
      )}

      {(session.growth_dimensions_touched || []).length > 0 && (
        <div className="h4u-card p-7 mt-5">
          <div className="h-sans" style={{color:"var(--muted)", fontSize:12, textTransform:"uppercase", letterSpacing:"0.08em"}}>Dimensions this touched</div>
          <div className="flex flex-wrap gap-2 mt-2">
            {session.growth_dimensions_touched.map((d, i) => <div key={i} className="h4u-chip">{DIMENSION_LABELS[d] || d}</div>)}
          </div>
        </div>
      )}

      {session.suggested_next_category && (
        <div className="h4u-card p-7 mt-5">
          <div className="h-sans" style={{color:"var(--muted)", fontSize:12, textTransform:"uppercase", letterSpacing:"0.08em"}}>Suggested next practice</div>
          <div className="h-sans mt-2" style={{fontWeight:600}}>{CATEGORY_LABELS[session.suggested_next_category] || session.suggested_next_category}</div>
        </div>
      )}

      <div className="mt-6 flex gap-3">
        <button className="h4u-btn h4u-btn-primary" onClick={onDone} data-testid="back-to-practices">Back to practices</button>
      </div>
    </div>
  );
}

// =============== Referrals ===============

// =============== Settings (Account) ===============
function Settings() {
  const { authHeaders, refresh, setToken } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await apiGet("/account/summary", authHeaders());
      setData(r.data);
      const p = await apiGet("/payments/products", authHeaders());
      setProducts(p.data.products || []);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not load account."); }
  }, [authHeaders]);
  useEffect(() => { load(); }, [load]);

  // -------- Email update --------
  const [newEmail, setNewEmail] = useState("");
  const [emailPwd, setEmailPwd] = useState("");
  const submitEmail = async (e) => {
    e.preventDefault(); setBusy(true); setMsg(""); setErr("");
    try {
      await apiPost("/account/update-email", { new_email: newEmail, current_password: emailPwd }, authHeaders());
      setMsg("Email updated."); setNewEmail(""); setEmailPwd("");
      await refresh(); load();
    } catch (e) { setErr(e?.response?.data?.detail || "Could not update email."); }
    finally { setBusy(false); }
  };

  // -------- Password update --------
  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [newPwd2, setNewPwd2] = useState("");
  const submitPwd = async (e) => {
    e.preventDefault(); setBusy(true); setMsg(""); setErr("");
    if (newPwd !== newPwd2) { setErr("New passwords don't match."); setBusy(false); return; }
    if (newPwd.length < 8)   { setErr("New password must be at least 8 characters."); setBusy(false); return; }
    try {
      await apiPost("/account/update-password", { current_password: oldPwd, new_password: newPwd }, authHeaders());
      setMsg("Password updated."); setOldPwd(""); setNewPwd(""); setNewPwd2("");
    } catch (e) { setErr(e?.response?.data?.detail || "Could not update password."); }
    finally { setBusy(false); }
  };

  // -------- Upgrade --------
  const checkout = async (tier) => {
    setBusy(true); setErr("");
    try {
      const r = await apiPost("/payments/checkout", { product_id: tier, origin_url: window.location.origin }, authHeaders());
      window.location.href = r.data.url;
    } catch (e) { setErr(e?.response?.data?.detail || "Could not start checkout."); }
    finally { setBusy(false); }
  };

  // -------- Cancel subscription --------
  const [cancelPwd, setCancelPwd] = useState("");
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const submitCancel = async () => {
    if (!cancelConfirm) { setErr("Please tick the confirm box."); return; }
    setBusy(true); setMsg(""); setErr("");
    try {
      const r = await apiPost("/subscription/cancel", { confirm: true }, authHeaders());
      setMsg(`Subscription cancelled — tier reverted to ${r.data.current_tier}.`);
      setCancelConfirm(false); load();
    } catch (e) { setErr(e?.response?.data?.detail || "Could not cancel."); }
    finally { setBusy(false); }
  };

  // -------- Unlink partner --------
  const [unlinkPwd, setUnlinkPwd] = useState("");
  const [unlinkConfirm, setUnlinkConfirm] = useState(false);
  const submitUnlink = async () => {
    if (!unlinkConfirm) { setErr("Please tick the confirm box."); return; }
    setBusy(true); setMsg(""); setErr("");
    try {
      await apiPost("/couple/unlink", { current_password: unlinkPwd || null, confirm: true }, authHeaders());
      setMsg("You have been unlinked from your partner.");
      setUnlinkConfirm(false); setUnlinkPwd(""); load();
    } catch (e) { setErr(e?.response?.data?.detail || "Could not unlink."); }
    finally { setBusy(false); }
  };

  // -------- Delete account --------
  const [delPwd, setDelPwd] = useState("");
  const [delPhrase, setDelPhrase] = useState("");
  const submitDelete = async () => {
    setBusy(true); setMsg(""); setErr("");
    try {
      await apiPost("/account/delete", { current_password: delPwd || null, confirm_phrase: delPhrase }, authHeaders());
      setToken(null);
      navigate("/");
    } catch (e) { setErr(e?.response?.data?.detail || "Could not delete."); setBusy(false); }
  };

  if (!data) return <Shell><div className="h4u-card p-7 mt-10">Loading account…</div></Shell>;

  const tier = data.couple?.tier || "free";
  const tierLabel = { free: "Free", plus: "Plus", premium: "Premium" }[tier] || tier;

  return (<Shell>
    <div className="mt-6" data-testid="settings-root">
      <div className="h-sans" style={{color:"var(--muted)", letterSpacing:"0.08em", textTransform:"uppercase", fontSize:12}}>Settings</div>
      <h2 className="text-3xl mt-1" style={{fontWeight:500}}>Your account</h2>

      {msg && <div className="h4u-banner mt-4" data-testid="settings-msg">{msg}</div>}
      {err && <div className="h4u-banner danger mt-4" data-testid="settings-err">{err}</div>}

      {/* Membership */}
      <div className="h4u-card p-7 mt-6" data-testid="settings-membership">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <div className="h-sans" style={{color:"var(--muted)", fontSize:12, textTransform:"uppercase", letterSpacing:"0.08em"}}>Membership</div>
            <div className="text-2xl mt-1" style={{fontWeight:500}}>{tierLabel} tier</div>
          </div>
          {data.couple?.subscription_cancelled_at && <div className="h4u-chip" data-testid="cancel-marker">Cancelled {new Date(data.couple.subscription_cancelled_at).toLocaleDateString()}</div>}
        </div>

        {tier !== "premium" && (
          <div className="mt-5">
            <div className="h-sans" style={{fontWeight:600, marginBottom:8}}>Upgrade your couple</div>
            <p className="h-sans" style={{color:"var(--ink-soft)", fontSize:14, lineHeight:1.55, marginBottom:12}}>
              These are <strong>monthly subscriptions for the whole couple</strong> — <em>either of you</em> can subscribe from your account, and both of you get access. No double billing. Cancel anytime from this page.
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              {products.filter(p => p.tier !== tier && p.tier !== "free").map(p => (
                <div key={p.tier} className="p-5 rounded-xl" style={{background:"#f3efe3", border:"1px solid var(--line)"}} data-testid={`upgrade-${p.tier}`}>
                  <div className="h-sans" style={{fontWeight:600, fontSize:18}}>{p.name}</div>
                  <div className="h-sans mt-1" style={{color:"var(--terracotta)", fontWeight:600, fontSize:22}} data-testid={`upgrade-${p.tier}-price`}>${p.amount.toFixed(2)}<span style={{fontSize:14, color:"var(--muted)", fontWeight:400}}> / month</span></div>
                  <div className="h-sans mt-1" style={{color:"var(--muted)", fontSize:12, textTransform:"uppercase", letterSpacing:"0.06em"}}>For the couple · monthly · cancel anytime</div>
                  {p.blurb && <p className="h-sans mt-2" style={{color:"var(--ink-soft)", fontSize:13, lineHeight:1.5}}>{p.blurb}</p>}
                  <button className="h4u-btn h4u-btn-primary mt-3" onClick={()=>checkout(p.tier)} disabled={busy} data-testid={`upgrade-${p.tier}-btn`}>Subscribe for us · ${p.amount.toFixed(2)}/mo</button>
                </div>
              ))}
            </div>
          </div>
        )}
        {tier === "premium" && !data.couple?.subscription_cancelled_at && (
          <div className="mt-4 p-4 rounded-xl" style={{background:"#f0eee5", border:"1px solid var(--line)"}} data-testid="premium-active">
            <div className="h-sans" style={{fontWeight:600}}>Your couple has Premium access.</div>
            <p className="h-sans mt-1" style={{color:"var(--ink-soft)", fontSize:13, lineHeight:1.55}}>One monthly subscription covers both of you. Cancel anytime from the section below.</p>
          </div>
        )}

        {tier !== "free" && !data.couple?.subscription_cancelled_at && (
          <div className="mt-6 p-5 rounded-xl" style={{background:"#f7f3ec", border:"1px solid var(--line)"}} data-testid="cancel-panel">
            <div className="h-sans" style={{fontWeight:600}}>Cancel monthly subscription</div>
            <p className="h-sans mt-1" style={{color:"var(--ink-soft)", fontSize:14, lineHeight:1.55}}>Cancelling stops future monthly billing and reverts your couple to the Free tier immediately. You keep any reports and progress you already have.</p>
            <label className="flex gap-2 items-center h-sans text-sm mt-3">
              <input type="checkbox" checked={cancelConfirm} onChange={e=>setCancelConfirm(e.target.checked)} data-testid="cancel-confirm" /> I understand and want to cancel.
            </label>
            <button onClick={submitCancel} disabled={!cancelConfirm || busy} className="h4u-btn h4u-btn-ghost mt-3" data-testid="cancel-submit">Cancel my subscription</button>
          </div>
        )}
      </div>

      {/* Partner link */}
      <div className="h4u-card p-7 mt-5" data-testid="settings-partner">
        <div className="h-sans" style={{color:"var(--muted)", fontSize:12, textTransform:"uppercase", letterSpacing:"0.08em"}}>Partner</div>
        <div className="text-xl mt-1" style={{fontWeight:500}}>
          {data.partner ? `${data.partner.name || data.partner.email}` : (data.couple ? "Not yet linked — waiting on your partner" : "You are not linked to anyone yet")}
        </div>
        {data.couple && (
          <div className="h-sans mt-2" style={{color:"var(--muted)", fontSize:13}}>Invite code: <span style={{letterSpacing:"0.2em", fontWeight:600}}>{data.couple.invite_code}</span></div>
        )}
        {data.couple && (
          <div className="mt-5 p-5 rounded-xl" style={{background:"#f7f3ec", border:"1px solid var(--line)"}} data-testid="unlink-panel">
            <div className="h-sans" style={{fontWeight:600}}>Unlink from partner</div>
            <p className="h-sans mt-1" style={{color:"var(--ink-soft)", fontSize:14, lineHeight:1.55}}>This removes your access to the shared report and practices tied to this partnership. Your own private answers will be deleted. Your partner keeps their private data.</p>
            {data.user.has_password && (
              <input type="password" className="h4u-input mt-3" placeholder="Confirm with your password" value={unlinkPwd} onChange={e=>setUnlinkPwd(e.target.value)} data-testid="unlink-pwd" />
            )}
            <label className="flex gap-2 items-center h-sans text-sm mt-3">
              <input type="checkbox" checked={unlinkConfirm} onChange={e=>setUnlinkConfirm(e.target.checked)} data-testid="unlink-confirm" /> I understand and want to unlink.
            </label>
            <button onClick={submitUnlink} disabled={!unlinkConfirm || busy} className="h4u-btn h4u-btn-ghost mt-3" data-testid="unlink-submit">Unlink</button>
          </div>
        )}
      </div>

      {/* Email */}
      <div className="h4u-card p-7 mt-5" data-testid="settings-email">
        <div className="h-sans" style={{color:"var(--muted)", fontSize:12, textTransform:"uppercase", letterSpacing:"0.08em"}}>Email</div>
        <div className="h-sans mt-1" style={{fontWeight:500}}>{data.user.email}</div>
        {data.user.has_password && (
          <form onSubmit={submitEmail} className="mt-4 space-y-3">
            <input type="email" className="h4u-input" placeholder="New email" value={newEmail} onChange={e=>setNewEmail(e.target.value)} required data-testid="new-email" />
            <input type="password" className="h4u-input" placeholder="Current password" value={emailPwd} onChange={e=>setEmailPwd(e.target.value)} required data-testid="new-email-pwd" />
            <button disabled={busy} className="h4u-btn h4u-btn-primary" data-testid="update-email-btn">Update email</button>
          </form>
        )}
        {!data.user.has_password && <div className="h-sans mt-2" style={{color:"var(--muted)", fontSize:13}}>You signed in with Google — your email is managed there.</div>}
      </div>

      {/* Password */}
      {data.user.has_password && (
        <div className="h4u-card p-7 mt-5" data-testid="settings-password">
          <div className="h-sans" style={{color:"var(--muted)", fontSize:12, textTransform:"uppercase", letterSpacing:"0.08em"}}>Password</div>
          <form onSubmit={submitPwd} className="mt-3 space-y-3">
            <input type="password" className="h4u-input" placeholder="Current password" value={oldPwd} onChange={e=>setOldPwd(e.target.value)} required data-testid="old-pwd" />
            <input type="password" className="h4u-input" placeholder="New password (min 8 chars)" value={newPwd} onChange={e=>setNewPwd(e.target.value)} required data-testid="new-pwd" />
            <input type="password" className="h4u-input" placeholder="Confirm new password" value={newPwd2} onChange={e=>setNewPwd2(e.target.value)} required data-testid="new-pwd-2" />
            <button disabled={busy} className="h4u-btn h4u-btn-primary" data-testid="update-pwd-btn">Update password</button>
          </form>
        </div>
      )}

      {/* Delete account */}
      <div className="h4u-card p-7 mt-5" style={{borderColor:"#e7c0ad"}} data-testid="settings-delete">
        <div className="h-sans" style={{color:"#7a3018", fontSize:12, textTransform:"uppercase", letterSpacing:"0.08em"}}>Danger zone</div>
        <div className="text-xl mt-1" style={{fontWeight:500}}>Delete account</div>
        <p className="h-sans mt-2" style={{color:"var(--ink-soft)", lineHeight:1.55, fontSize:14}}>
          This permanently deletes your account and your private answers. If your partner is still linked, they will keep their own private data but lose the pairing.
        </p>
        <div className="mt-3 space-y-3">
          {data.user.has_password && (
            <input type="password" className="h4u-input" placeholder="Current password" value={delPwd} onChange={e=>setDelPwd(e.target.value)} data-testid="del-pwd" />
          )}
          <input type="text" className="h4u-input" placeholder='Type: DELETE MY ACCOUNT' value={delPhrase} onChange={e=>setDelPhrase(e.target.value)} data-testid="del-phrase" />
          <button onClick={submitDelete} disabled={busy || delPhrase.trim() !== "DELETE MY ACCOUNT"} className="h4u-btn h4u-btn-ghost" style={{borderColor:"#e7c0ad", color:"#7a3018"}} data-testid="del-btn">Delete my account</button>
        </div>
      </div>
    </div>
  </Shell>);
}


function Referrals() {
  const { authHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => { (async()=>{ try { const r = await apiGet("/referrals/me", authHeaders()); setData(r.data); } catch (e) { console.debug("[hope4us:swallowed]", e); } })(); }, [authHeaders]);
  if (!data) return <Shell><div className="h4u-card p-7 mt-10">Loading…</div></Shell>;
  const shareUrl = `${window.location.origin}/auth?ref=${data.referral_code}`;
  const copy = () => { navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(()=>setCopied(false), 2000); };
  return (<Shell>
    <div className="mt-6">
      <div className="h-sans" style={{color:"var(--muted)", textTransform:"uppercase", letterSpacing:"0.08em", fontSize:12}}>Refer a friend</div>
      <h2 className="text-3xl mt-1" style={{fontWeight:500}}>Give a free month — get a free month</h2>
      <p className="h-sans mt-2 max-w-2xl" style={{color:"var(--ink-soft)", lineHeight:1.6}}>{data.benefit} No marketing urgency — just two relationships growing instead of one.</p>
    </div>
    <div className="h4u-card p-7 mt-6">
      <div className="h-sans" style={{color:"var(--muted)", fontSize:12, textTransform:"uppercase", letterSpacing:"0.08em"}}>Your referral code</div>
      <div className="text-4xl mt-2" style={{fontWeight:500, letterSpacing:"0.15em", color:"var(--terracotta)"}} data-testid="referral-code">{data.referral_code}</div>
      <div className="h-sans mt-5" style={{color:"var(--ink-soft)"}}>Shareable link:</div>
      <div className="flex items-center gap-3 mt-2">
        <input className="h4u-input" readOnly value={shareUrl} data-testid="referral-link" />
        <button onClick={copy} className="h4u-btn h4u-btn-primary" data-testid="copy-referral">{copied ? "Copied" : "Copy"}</button>
      </div>
      <div className="grid md:grid-cols-3 gap-4 mt-7">
        <div className="p-4 rounded-xl" style={{background:"#f3efe3", border:"1px solid var(--line)"}}><div className="h-sans" style={{color:"var(--muted)", fontSize:12, textTransform:"uppercase"}}>Friends invited</div><div className="text-3xl mt-1" style={{fontWeight:500}}>{data.referred_count}</div></div>
        <div className="p-4 rounded-xl" style={{background:"#f3efe3", border:"1px solid var(--line)"}}><div className="h-sans" style={{color:"var(--muted)", fontSize:12, textTransform:"uppercase"}}>Premium conversions</div><div className="text-3xl mt-1" style={{fontWeight:500}}>{data.converted_count}</div></div>
        <div className="p-4 rounded-xl" style={{background:"#f3efe3", border:"1px solid var(--line)"}}><div className="h-sans" style={{color:"var(--muted)", fontSize:12, textTransform:"uppercase"}}>Premium credit days</div><div className="text-3xl mt-1" style={{fontWeight:500, color:"var(--sage-2)"}}>{data.credit_days}</div></div>
      </div>
    </div>
  </Shell>);
}

// =============== Upgrade / Payments ===============
const TIERS = [
  {
    id: "free", name: "Free Trial", price: "$0", priceNote: "",
    questions: "~28 questions · ~10 min each",
    features: [
      "Snapshot summary",
      "Top strength + top growth area",
      "One pattern cluster",
      "Attachment + love-language quick read",
      "One script + one question to ask each other",
    ],
  },
  {
    id: "plus", name: "Plus", price: "$19.99", priceNote: "per month · for the couple",
    questions: "~52 questions",
    features: [
      "Everything in Free",
      "Full pattern clusters",
      "Each partner's likely experience",
      "Alignment + mismatch + 6-dimension compatibility",
      "Candidness summary",
      "7-day plan + 4 scripts + interventions",
      "Either partner subscribes · both get access",
      "Cancel anytime from your Settings page",
    ],
  },
  {
    id: "premium", name: "Premium Membership", price: "$29.99", priceNote: "per month · for the couple",
    questions: "Full 82-question intake",
    features: [
      "Everything in Plus",
      "Per-partner candidness map (avoided topics + short & long-term impact on the other partner)",
      "Premarital / commitment-readiness module (finances, family, kids, intimacy, dealbreakers)",
      "Adaptive, guided practices with in-step feeling check-ins",
      "9-dimension progress tracking + milestones",
      "Risk + protective factors",
      "Either partner subscribes · both get access",
      "Cancel anytime from your Settings page",
    ],
  },
];

function Upgrade() {
  const { authHeaders } = useAuth();
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [currentTier, setCurrentTier] = useState("free");

  useEffect(() => { (async()=>{ try { const r = await apiGet("/couple/me", authHeaders()); setCurrentTier(r.data?.couple?.tier || "free"); } catch (e) { console.debug("[hope4us:swallowed]", e); } })(); }, [authHeaders]);

  const buy = async (id) => {
    setBusy(id); setErr("");
    try {
      const r = await apiPost("/payments/checkout", { product_id: id, origin_url: window.location.origin }, authHeaders());
      window.location.href = r.data.url;
    } catch (e) { setErr(e?.response?.data?.detail || "Checkout failed."); setBusy(""); }
  };

  return (<Shell>
    <div className="mt-6">
      <div className="h-sans" style={{color:"var(--muted)", textTransform:"uppercase", letterSpacing:"0.08em", fontSize:12}}>Choose your depth</div>
      <h2 className="text-3xl mt-1" style={{fontWeight:500}}>Free is enough to start. Plus and Premium go deeper.</h2>
      <p className="h-sans mt-2 max-w-2xl" style={{color:"var(--ink-soft)", lineHeight:1.6}}>Every tier works for every kind of couple — new, healthy, premarital, conflict, repair. Tier = depth. No fear-based selling.</p>
    </div>
    {err && <div className="h4u-banner danger mt-4">{err}</div>}
    <div className="grid md:grid-cols-3 gap-5 mt-8">
      {TIERS.map(t => {
        const isCurrent = t.id === currentTier;
        const isLower = TIER_ORDER.indexOf(t.id) < TIER_ORDER.indexOf(currentTier);
        return (
          <div key={t.id} className="h4u-card p-7 relative" data-testid={`tier-${t.id}`} style={t.id === "premium" ? {borderColor:"var(--terracotta)", boxShadow:"0 30px 60px -30px rgba(184,107,75,0.4)"} : {}}>
            {t.id === "premium" && <div className="h4u-chip" style={{position:"absolute", top:14, right:14, background:"var(--terracotta)", color:"#fffdf7", border:"none"}}>Most depth</div>}
            <div className="h-sans" style={{fontSize:13, color:"var(--terracotta)", letterSpacing:"0.08em", textTransform:"uppercase"}}>{t.name}</div>
            <div className="mt-3 flex items-baseline gap-1"><div className="text-4xl" style={{fontWeight:500}}>{t.price}</div><div className="h-sans" style={{color:"var(--muted)"}}>{t.priceNote}</div></div>
            <div className="h-sans mt-1" style={{color:"var(--ink-soft)"}}>{t.questions}</div>
            <ul className="mt-4 space-y-2 h-sans" style={{color:"var(--ink-soft)", fontSize:14, lineHeight:1.55}}>
              {t.features.map((f,i)=>(<li key={i} className="flex gap-2"><span style={{color:"var(--sage-2)"}}>✓</span><span>{f}</span></li>))}
            </ul>
            <div className="mt-5">
              {isCurrent && <div className="h4u-chip">Your current tier</div>}
              {!isCurrent && t.id === "free" && !isLower && <div className="h-sans text-sm" style={{color:"var(--muted)"}}>Default tier — no purchase needed</div>}
              {!isCurrent && t.id !== "free" && !isLower && (
                <button onClick={()=>buy(t.id)} disabled={busy===t.id} className={`h4u-btn w-full ${t.id === "premium" ? "h4u-btn-terra" : "h4u-btn-primary"}`} data-testid={`buy-${t.id}`}>
                  {busy===t.id ? "Redirecting…" : (t.id === "premium" ? "Start Premium" : "Get Plus")}
                </button>
              )}
              {isLower && <div className="h-sans text-sm" style={{color:"var(--muted)"}}>You already have a higher tier</div>}
            </div>
          </div>
        );
      })}
    </div>
  </Shell>);
}
const TIER_ORDER = ["free","plus","premium"];

function PaymentSuccess() {
  const { authHeaders } = useAuth();
  const [status, setStatus] = useState("checking");
  const [err, setErr] = useState("");
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) { setErr("Missing session id."); setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const r = await apiGet(`/payments/status/${sid}`, authHeaders());
        if (r.data.payment_status === "paid") { setStatus("paid"); return; }
        if (r.data.status === "expired") { setStatus("expired"); return; }
        if (attempts >= 8) { setStatus("pending"); return; }
        setTimeout(poll, 2000);
      } catch (e) { setErr("Could not verify."); setStatus("error"); }
    };
    poll();
  }, [authHeaders]);

  return (<Shell><div className="h4u-card p-8 mt-10 max-w-md mx-auto" data-testid="payment-status">
    {status === "checking" && "Confirming your payment…"}
    {status === "paid" && (<><div className="text-2xl" style={{fontWeight:500}}>Payment received</div><p className="h-sans mt-2" style={{color:"var(--ink-soft)"}}>Your full report is unlocked.</p><Link to="/report" className="h4u-btn h4u-btn-primary mt-4 inline-flex">View report</Link></>)}
    {status === "pending" && "Payment is processing. Check your email for confirmation."}
    {status === "expired" && "This session expired. Please try again."}
    {status === "error" && (err || "Something went wrong.")}
  </div></Shell>);
}

function PaymentCancel() {
  return (<Shell><div className="h4u-card p-8 mt-10 max-w-md mx-auto"><div className="text-2xl" style={{fontWeight:500}}>Checkout cancelled</div><Link to="/upgrade" className="h4u-link mt-3 inline-block">Back to upgrade</Link></div></Shell>);
}

// =============== Resources ===============
const ICONS = { alert: "🚨", chat: "💬", compass: "🧭", shield: "🧠", heart: "🧩", therapist: "🛋️" };

function Resources() {
  const [data, setData] = useState(null);
  useEffect(() => { (async()=>{ const r = await apiGet("/resources"); setData(r.data); })(); }, []);
  if (!data) return <Shell><div className="h4u-card p-7 mt-10">Loading…</div></Shell>;
  return (<Shell>
    <h2 className="text-3xl mt-6" style={{fontWeight:500}}>If you ever need more support</h2>
    <p className="h-sans mt-3 max-w-3xl" style={{color:"var(--ink-soft)", lineHeight:1.65}}>{data.intro}</p>

    <div className="space-y-6 mt-8">
      {data.groups.map((g, gi) => (
        <div key={gi} className="h4u-card p-7" data-testid={`resource-group-${gi}`}>
          <div className="flex items-center gap-3">
            <span style={{fontSize:22}}>{ICONS[g.icon] || "•"}</span>
            <h3 className="text-xl" style={{fontWeight:500}}>{g.title}</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            {g.items.map((it, ii) => (
              <div key={ii} className="p-5 rounded-xl" style={{background:"#f3efe3", border:"1px solid var(--line)"}} data-testid={`resource-${gi}-${ii}`}>
                <div className="h-sans" style={{fontWeight:600, fontSize:16}}>{it.name}</div>
                <div className="h-sans mt-2 space-y-1" style={{color:"var(--ink-soft)", fontSize:14, lineHeight:1.55}}>
                  {it.url && <div>🌐 <a className="h4u-link" href={it.url} target="_blank" rel="noreferrer">{it.url.replace(/^https?:\/\//,"")}</a></div>}
                  {it.phone && <div>📞 <strong>{it.phone}</strong></div>}
                  {it.text && <div>📱 Text <strong>{it.text}</strong></div>}
                  {it.chat && <div>💻 Live chat available</div>}
                  {it.note && <div className="mt-2" style={{fontStyle:"italic", color:"var(--muted)"}}>{it.note}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>

    <div className="h4u-banner mt-8" data-testid="resources-note">{data.note}</div>
    {data.emergency && <div className="h4u-banner danger mt-3" data-testid="resources-emergency">{data.emergency}</div>}
  </Shell>);
}

// =============== App ===============
function AppRouter() {
  const location = useLocation();
  // Check URL fragment for session_id from Emergent OAuth before any other routing
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/about" element={<About />} />
      <Route path="/auth" element={<AuthScreen />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/legal" element={<Legal scrollGate={true} showAcceptControl={true} />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/resources" element={<Resources />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/intake" element={<ProtectedRoute><Intake /></ProtectedRoute>} />
      <Route path="/report" element={<ProtectedRoute><ReportView /></ProtectedRoute>} />
      <Route path="/upgrade" element={<ProtectedRoute><Upgrade /></ProtectedRoute>} />
      <Route path="/practices" element={<ProtectedRoute><Practices /></ProtectedRoute>} />
      <Route path="/practice/:id" element={<ProtectedRoute><PracticeSession /></ProtectedRoute>} />
      <Route path="/progress" element={<ProtectedRoute><Progress /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/referrals" element={<ProtectedRoute><Referrals /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="*" element={<Landing />} />
    </Routes>
  );
}

// =============== ErrorBoundary — friendly fallback on unexpected crashes ===============
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, message: (error && error.message) ? error.message : String(error) };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("Hope4US UI crash:", error, info);
    // Fire-and-forget telemetry to the backend so we can triage crashes we can't reproduce locally.
    try {
      const token = localStorage.getItem("h4u_token");
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;
      fetch(`${API}/errors`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          message: (error && error.message) ? error.message : String(error),
          stack: error && error.stack ? String(error.stack) : "",
          component_stack: info && info.componentStack ? String(info.componentStack) : "",
          url: typeof window !== "undefined" ? window.location.href : "",
          user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        }),
        keepalive: true,
      }).catch(() => {});
    } catch (e) { /* never let telemetry break the fallback */ }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{minHeight:"100vh", background:"var(--paper)", padding:"48px 24px", fontFamily:"'Outfit', sans-serif"}}>
          <div style={{maxWidth:"640px", margin:"40px auto"}} className="h4u-card p-8" data-testid="ui-crash-fallback">
            <div className="h-sans" style={{color:"var(--muted)", fontSize:12, textTransform:"uppercase", letterSpacing:"0.08em"}}>Something went sideways</div>
            <h2 className="text-2xl mt-1" style={{fontWeight:500}}>Sorry — that page hit an unexpected error.</h2>
            <p className="h-sans mt-3" style={{color:"var(--ink-soft)", lineHeight:1.6}}>Refreshing usually clears it. We've logged what happened; if it keeps occurring, please contact us and share what you were doing.</p>
            <div className="flex gap-3 mt-5">
              <button onClick={() => { window.location.href = "/dashboard"; }} className="h4u-btn h4u-btn-primary" data-testid="ui-crash-back">Back to dashboard</button>
              <button onClick={() => window.location.reload()} className="h4u-btn h4u-btn-ghost" data-testid="ui-crash-refresh">Refresh</button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AgreementGate />
        <ErrorBoundary>
          <AppRouter />
        </ErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  );
}
